import _ from 'lodash';
import { MaxNumberOfSupportFiles, MaxSupportFileSizeInMB } from '../models/constants/support.constants';
import { tableSidePanelShortcuts } from './utils';
import {
  IGetSpecificResourceDefinitionActionLabels as IGetSpecificRawDataLabels,
  IWebAppLabels,
} from './webAppLabels.interface';
import { ResourceType } from '../models/api';

export const Labels: IWebAppLabels = {
  violationRulesPage: {
    platforms: {
      powerPlatform: 'Power platform',
      workato: 'Workato',
    },
    selectIntegrationPlaceHolder: 'Integrations',
    defaultRuleDescription:
      "Governs Environments t haven't been assigned manually & resources residing outside the Environments space",
    exemptionsListTitle: 'Exemption list',
    statusChangedSuccessfullyMessage: 'Status changed',
    overviewTitle: 'Overview',
    overviewTiles: {
      ruleDetailsTile: {
        title: 'Policy Details',
        category: 'Category',
        attackLink: 'ATT&CK',
      },
      scopeTile: {
        title: 'Integration',
        platform: 'Platform',
        name: 'Name',
        id: 'ID',
      },
    },
    statusTitle: 'Status',
    statuses: {
      active: 'Active',
      inActive: 'Inactive',
    },
    severity: 'Severity',
    count: 'Count',
    ruleTags: 'Rule tags',
    ruleIdTitle: 'Policy ID',
    exemptionsList: {
      resourceNameCol: 'Resource name',
      resourceLinkCol: 'Resource link',
      dateAddedToExemptionCol: 'Date added to exemption',
      exemptionAddedByCol: 'Added by',
    },
    ruleActions: {
      disableButton: 'Disable',
      enableButton: 'Enable',
      editButton: 'Edit',
      resetButton: 'Reset',
    },
    edit: {
      title: 'Edit Violation Rule',
      changeSeverity: 'Change Severity',
      changeStatus: 'Change Status',
    },
  },
  dashboardNewOrgPage: {
    gettingStarted: 'Getting Started',
    platforms: 'Agentic Platforms',
    userGuide: 'User guide',
    blog: 'AI agent security blog',
    contactUs: 'Have a question?',
  },
  accessRestricted: {
    notification: 'Access restricted. Reach out to your admin to request access.',
  },
  mainDashboardPage: {
    openViolationsTitle: 'Open Violations',
    openViolationsTooltip: 'Summarizes all Open violations based on the severity',
    riskMappingScoreWidget: {
      title: 'Resource Risk Score',
      tooltip: 'Identifies the high risk & high business impact resources that require urgent attention',
      securityRisk: 'Security Risk',
      securityRiskHoverMsg: 'Evaluates all violations of a resource and selects the highest severity',
      businessCriticality: 'Business Criticality',
      businessCriticalityHoverMsg: 'Evaluates the potential business importance of a Flow, Application or Connection',
      criticalRisk: 'Critical',
      highRisk: 'High',
      mediumRisk: 'Medium',
      lowRisk: 'Low',
      minimalRisk: 'Minimal',
      healthyRisk: 'Healthy',
    },
    runtimeRiskBreakdownTitle: 'Runtime Risk Breakdown',
    runtimeRiskBreakdownTooltip: 'Displays number of runtime findings by service',
    lowCodeNoCodeAdoptionTitle: 'Adoption & Usage Trend',
    lowCodeNoCodeAdoptionTooltip: 'Displays the number of resources overtime',
    topOwaspMitreWidget: {
      title: 'Top OWASP/MITRE',
      tooltip: 'Groups all GA security rule violations according to the OWASP and MITRE frameworks',
      categoriesTitle: 'Categories',
      violationsTitle: 'Violations',
    },
    passedAndFailedRulesTitle: 'Passed & Failed Security Rules',
    passedAndFailedRulesTooltip: 'Rules that triggered or did not trigger violations',
    agentsByPlatformTitle: 'Agents by Platform',
    agentsByPlatformTooltip: 'Distribution of active agents across different platforms',
  },
  visibilityDashboardPage: {
    totalTitle: 'Total',
    buildersAdoptionAndGrowthTitle: 'Builders Adoption & Growth',
    topBuildersTitle: 'Top Builders',
    topBuildersTooltip: 'Identifies the Low-Code/No-Code champions in the organization, who built the most resources',
    topBuildersCreatorTitle: 'Created By',
    topBuildersApplicationTitle: 'Application',
    topBuildersAutomationTitle: 'Automation',
    topBuildersAgentTitle: 'Agent',
    topBuildersDataStorageTitle: 'Data Storage',
    buildersAdoptionTitle: 'New Builders Adoption',
    buildersAdoptionTooltip:
      'Displays how many new builders have participated in creation of new resources, grouped by the resource category in the last year',
    managementAndCleanupTitle: 'Management & Clean-up',
    unusedResourcesTitle: 'Unused Resources',
    unusedResourcesTooltip:
      'Identifies cases where a resources is no longer being used and could be a candidate for deletion, grouped by the resource category',
    orphanResourcesTitle: 'Orphan Resources',
    orphanResourcesTooltip:
      'Identifies cases where a resource no longer has an owner and needs to be reassigned or deleted, grouped by the resource category',
    resourceAnalysisTitle: 'Resource Analysis',
  },
  enterpriseAiDashboardPage: {
    openViolationsSeverityDistributionForAiTitle: 'Plugins Risk Severity',
    openViolationsSeverityDistributionForAiTooltip:
      'Identifies the riskiest plugins in the organization, grouped by the severity of the violations',
    m365copilotPluginTypesDistributionTitle: 'Plugin Types',
    m365copilotPluginTypesDistributionTooltip: 'Identifies the most common plugin types in the organization',
    copilotsAdoptionTitle: 'Usage Trends',
    copilotsAdoptionTooltip:
      'Displays the number of users and interactions with copilot in the entire organization overtime',
    owaspRiskDistributionForAiTitle: 'Risk Distribution',
    owaspRiskDistributionForAiTooltip:
      'Identifies the riskiest OWASP categories in the organization that propose and immediate risk on ai plugins',
    m365copilotTopPluginsDisplayNameTitle: 'Plugin Name',
    m365copilotTopPluginsTypeTitle: 'Plugin Type',
    m365copilotTopPluginsInteractionsTitle: 'Interactions',
  },
  menu: {
    aiSpmPage: 'AISPM',
    aiObservabilityPage: 'AI Observability',
    aiDrPage: 'AIDR',
    aiDetectionFindingsPage: 'Findings',
    aiDetectionActivityPage: 'Activity',
    aiDetectionPreventionPage: 'Prevention',
    aiDetectionDataInsightsPage: 'Data Insights',
    dataLensPage: 'Data Lens',
    governancePage: 'Governance',
    aiDetectionInLineControlPage: 'In-Line Control',
    aiDetectionPlaybooksPage: 'AI Detection Playbooks',
    aiDetectionPlaybookPage: 'AI Detection Playbook',
    aiRuntimePage: 'AI Runtime',
    aiRuntimeDetectionPage: 'AI Runtime Detection',
    dashboardPage: 'Dashboard',
    adoptionPage: 'Adoption',
    enterpriseAiPage: 'Enterprise AI',
    enterpriseAiDetectionPage: 'AI Runtime',
    connectPage: 'Connect',
    integrationsPage: 'Integrations',
    adaptersPage: 'Adapters',
    violationsPage: 'Violations',
    hygienePage: 'Hygiene',
    issuesPage: 'Issues',
    policyPage: 'Policies',
    settingsPage: 'Settings',
    inventoryPage: 'Inventory',
    apiDocsPage: 'API Reference',
    changelogPage: 'Release Notes',
    docsPage: 'Docs',
    productGuides: 'Product Guides',
    apiReference: 'API Reference',
    playbooksPage: 'Playbooks',
    playbookPage: 'Playbook',
    redTeamingPage: 'AI Red Teaming',
    boundariesPage: 'Boundaries',
    logout: 'Log out',
    role: 'Your role is',
    roles: 'Your roles are',
  },
  tableActionsAndFilters: {
    actionsColumn: 'Actions',
    category: 'Category',
    type: 'Type',
    resourceStatus: 'Resource Status',
    environment: 'Environment',
    platform: 'Platform',
    firstSeenFilter: 'First Seen',
    fromDate: 'From',
    toDate: 'To',
    createdSinceDate: 'Created Since',
    timestampSinceDate: 'From',
    createdUntilDate: 'Created Until',
    timestampUntilDate: 'To',
    firstSeenSinceDate: 'First Seen Since',
    firstSeenUntilDate: 'First Seen Until',
    searchBoxText: 'Search by rule name or id',
    lastChangedFilter: 'Last Changed',
    severity: 'Severity',
    user: 'User',
    integrationNameFilter: 'Select Integrations',
    amountItemToShow: 'Results per Page',
    customViewText: 'Select pre-defined query...',
    lastExecutionSince: 'Execution Since',
    lastExecutionUntil: 'Execution Until',
    selectedRowsText: 'rows on this page are selected.',
    expandResultsText: 'Expand action to the filter result set',
    inventoryCustomViewDetails: {
      applicationsRecentlyModified: 'Recently Modified Applications',
      automationsRecentlyModified: 'Recently Modified Automations',
      newUsersCreation: 'New Users',
      externalIdentities: 'External Users',
      connectionCreatedFromCustomConnector: 'Custom Connections',
      notInUseConnection: 'Unused Connection',
      disabledConnections: 'Inactive Connections',
      disabledAutomations: 'Inactive Automations',
      inactiveUsers: 'Inactive Users',
      automationUsesAiModel: 'Automation Uses AI Model',
      teamsEnvironments: 'Teams Environments',
      autonomousAgents: 'Autonomous Agents',
      declarativeAgents: 'Declarative Agents',
      agentsUsingMcp: 'Agents Using MCP',
      automationUsesWebhook: 'Automations Using a Webhook',
      automationUsesHttpStepsWithoutAuthentication: 'Automation Using HTTP Without Authentication',
      agentTriggers: 'Agent Triggers',
      allBuilders: 'All builders',
      orgWideAccess: 'Org Wide Access',
      publicAccess: 'Public Access',
      shadowAi: 'Shadow AI',
      privilegedAgents: 'Privileged Agents',
    },
    violationsCustomViewDetails: {
      overSharedResources: 'Over Shared Resources',
      nonCompliantConnections: 'Non-Compliant Connections',
      insecureCommunication: 'Insecure Communication',
      authenticationIssues: 'Authentication Issues',
      customCode: 'Custom Code',
      potentiallyRiskyDataFlow: 'Potentially Risky Data Flow',
      suspiciousConfiguration: 'Suspicious Configuration',
      untrustedGuestAccess: 'Untrusted Guest Access',
      untrustedMemberAccess: 'Untrusted Member Access',
    },
  },
  integrationsPage: {
    title: 'Integrations',
    addButton: 'Create integration',
    integrationIdField: 'Integration ID',
    copyIntegrationsId: 'Copy ID',
    platformField: 'Platform',
    environmentField: 'Environment',
    integrationMethodField: 'Integration Method',
    createdAtField: 'Created at',
    createdByField: 'Created By',
    lastChangedField: 'Last Changed',
    lastChangedByField: 'Last Changed By',
    lastScan: 'Last Scan',
    scanDataNotFound: 'Scan data not found',
    pending1stScanResults: 'Pending 1st scan results',
    startedCollectingAt: 'Started collecting at',
    errorFetchingScanFreshnessData: 'Error fetching scan freshness data',
    status: 'Status',
    maxIntegrationsMsg: 'Your tenant has reached the maximum integrations quota',
    notAllowedToAddIntegrationMsg: 'You do not have sufficient permissions to add an integration',
    deleteConfirmation: {
      title: 'Delete integration',
      content:
        'Are you sure?<br>Deleting an integration will delete the associated policies, violations, hygiene issues and resources.',
    },
    integrationActions: {
      editAction: 'Edit',
      enableAction: 'Enable',
      disableAction: 'Disable',
      deleteAction: 'Delete',
      retryAction: 'Retry',
    },
  },
  adaptersPage: {
    title: 'Adapters',
    addButton: 'Add adapter',
    edit: 'Edit',
    enable: 'Enable',
    disable: 'Disable',
    delete: 'Delete',
    adapterNameField: 'Adapter Name',
    platformField: 'Platform',
    hecUrlField: 'HEC URL',
    hecTokenField: 'HEC Token',
    indexField: 'Index',
    createdAtField: 'Created at',
    createdByField: 'Created By',
    lastChangedField: 'Last Changed',
    lastChangedByField: 'Last Changed By',
    status: 'Status',
    maxAdaptersMsg: 'Your tenant has reached the maximum adapters quota',
    notAllowedToAddAdapterMsg: 'You do not have sufficient permissions to add an adapter',
    deleteConfirmation: {
      title: 'Delete adapter',
      content: 'Are you sure you want to delete this adapter?',
    },
  },
  integrationPage: {
    editTitle: 'Edit Integration',
    addTitle: 'Add New Integration',
    integrationNameField: 'Integration Name',
    createButton: 'Create',
    saveButton: 'Save',
    cancelButton: 'Cancel',
    addModePlatformField: 'Select Platform',
    editModePlatformField: 'Platform',
    addModeEnvironmentField: 'Select Environment',
    editModeEnvironmentField: 'Environment',
    addModeAuthMethodField: 'Select method',
    editModeAuthMethodField: 'Method',
    authMethodDeprecatedLabel: 'Deprecated',
    platformDropDownPlaceHolder: 'More Platforms',
    errorMessageOnUpdateCredentials: 'Something went wrong - could not update credentials',
    errorMessageOnUpdate: 'Something went wrong',
    authDetails: {
      apiKeyMethod: 'API key',
      userPasswordMethod: 'User/Password',
      certWithUserPasswordMethod: 'Client Certificate & User/Password',
      oauthMethod: 'OAuth Authorization Code',
      oktaSsoMethod: 'Okta SAML based SSO',
      apiTokenMethod: 'API token',
      servicePrincipalMethod: 'Service Principal',
      ppZenityMultiTenantAppServicePrincipalMethod: 'Managed Application Consent',
      oauthClientCredentialsFlowMethod: 'OAuth Client Credentials flow',
      jwtBearerFlowMethod: 'JWT Bearer Flow',
      iamRoleMethod: 'IAM Role',
      serviceAccountMethod: 'Service Account',
      serviceAccountManagedMethod: 'Managed Service Account',
      personalAccessTokenMethod: 'Access Token',
      enrollmentKeyMethod: 'Enrollment Key',
      cursorHooksMethod: 'Cursor Enterprise Hooks',
      apiKeyFieldKey: 'API key',
      emailFieldKey: 'Email',
      usernameFieldKey: 'Username',
      passwordFieldKey: 'Password',
      tenantIdFieldKey: 'Tenant id',
      clientIdFieldKey: 'Client id',
      oktaAppEmbeddedUrlFieldKey: 'Okta application embedded link',
      oktaUsernameFieldKey: 'Okta username',
      oktaPasswordFieldKey: 'Okta password',
      servicePrincipalSecretFieldKey: 'Service principal secret',
      clientCertificateThumbprintFieldKey: 'Client certificate thumbprint',
      clientCertificatePrivateKeyFieldKey: 'Client certificate private key',
      salesforceInstanceUrlFieldKey: 'Salesforce instance URL',
      apiTokenFieldKey: 'API token',
      clientSecretFieldKey: 'Client Secret',
      servicePrincipalObjectIdFieldKey: 'Service principal object id',
      serviceNowInstanceUrlFieldKey: 'ServiceNow instance URL',
      workspaceIdFieldKey: 'Workspace id',
      iamRoleARNFieldKey: 'IAM Role ARN',
      accountIdFieldKey: 'Account ID',
      customDomainFieldKey: 'Custom Domain',
      apiPathPrefixFieldKey: 'API Path Prefix',
      selectSelfHostedFieldKey: 'Select Hosting Type',
      externalIdFieldKey: 'External ID',
      organizationIdFieldKey: 'Organization ID',
      serviceAccountPrivateKeyDetailsFieldKey: 'Service Account Private Key Details',
      tokenUriFieldKey: 'Token URI',
      projectIdFieldKey: 'Project ID',
      privateKeyIdFieldKey: 'Private Key ID',
      universeDomainFieldKey: 'Universe Domain',
      clientEmailFieldKey: 'Client Email',
      privateKeyFieldKey: 'Private Key',
      personalAccessTokenFieldKey: 'Access Token',
      enterpriseAccountIdFieldKey: 'Enterprise Account ID',
      endpointEnrollmentKeyFieldKey: 'Endpoint enrollment key',
      outpostManagedIdentityMethod: 'Outpost Managed Identity',
      mtlsClientCertificateFieldKey: 'Client Certificate',
      mtlsClientPrivateKeyFieldKey: 'Client Private Key',
      mtlsCaCertificateFieldKey: 'CA Certificate',
      enableMtlsToggleLabel: 'Enable mTLS',
      enableMtlsToggleDescription: 'Enable mutual TLS authentication with the Budibase gateway',
    },
    authDescriptions: {
      ppUserPasswordUsername: 'Power Platform Administrator login email address',
      ppUserPasswordPass: 'Power Platform Administrator login password',
      ppTenantId: 'Azure Active Directory tenant ID',
      ppClientId: 'Azure Active Directory application ID (specified under Enterprise Applications)',
      ppServicePrincipalSecret: 'Azure Active Directory application secret (specified under App Registrations)',
      ppClientCertificateThumbprint:
        'Azure Active Directory application certificate thumbprint (specified under App Registrations)',
      ppClientCertificatePrivateKey: 'The private key of the application certificate',
      workatoUserPasswordUsername: 'Workato team owner or admin login email address',
      workatoUserPasswordPass: 'Workato team owner or admin login password',
      workatoApiKeyEmail: 'Workato team owner or admin login email address',
      workatoApiKeyKey: 'Workato account API key',
      workatoApiToken: 'Workato client API token',
      oktaAppEmbeddedUrl: 'Okta application embedded link',
      oktaUsername: 'The username to sign in to Okta application',
      oktaPassword: 'The password to sign in to Okta application',
      salesforceInstanceUrl: 'A URL indicating the Salesforce instance of the organization',
      salesforceUsername: 'Username in the Salesforce organization authorized to the Connected App',
      salesforceClientId: 'Salesforce Connected App Consumer Key (also known as Client id)',
      salesforceClientSecret: 'Salesforce Connected App Consumer Secret (also known as Client Secret)',
      salesforceCertificatePrivateKey: 'Private key of a Certificate associated with the Salesforce Connected App',
      servicePrincipalObjectId: 'The object id of the service principal of Zenity multi-tenant app',
      serviceNowInstanceUrl: 'A URL indicating the ServiceNow instance of the organization',
      serviceNowClientId: 'ServiceNow client ID',
      serviceNowSecretId: 'ServiceNow client secret',
      chatGptEnterpriseWorkspaceId: 'ChatGPT Enterprise Workspace ID',
      chatGptEnterpriseApiKey: 'ChatGPT Enterprise Workspace API key',
      budibaseApiKey: 'Budibase API Key for authentication',
      airtablePersonalAccessToken: 'Access Token for Airtable API authentication',
      airtableTenantId: 'Airtable Enterprise Account ID',
      amazonBedrockIAMRoleARN: 'AWS IAM Role ARN',
      amazonBedrockAccountId: 'AWS Account ID',
      amazonBedrockExternalId: "External ID associated with the IAM Role's trust policy",
      googleOrganizationId: 'Google Organization ID',
      googleServiceAccountPrivateKeyDetails:
        'JSON file contains the private key details for the service account as received from Google Cloud Platform',
      googleTokenUri: 'The OAuth 2.0 Token URI',
      googleProjectId: 'Google project ID associated with the service account credential (Optional)',
      privateKeyId: 'The private key ID (Optional)',
      googleUniverseDomain: 'The universe domain. The default universe domain is googleapis.com (Optional)',
      googleClientEmail: "The service account's email",
      googlePrivateKey: 'The private key for the service account',
      devicesUsername: 'Username',
      devicesPassword: 'Password',
      devicesEnrollmentKeyDescription:
        "Copy enrollment key in order to use it during Zenity's Endpoint agent installation",
      devicesEnrollmentKeyPreCreationInfo:
        'The Zenity Endpoint enrollment key is required during the installation of Zenity\'s Endpoint agent.\nClick "Create" to generate enrollment key.',
      devicesDownloadInstallerDescription: 'Download the latest endpoint installer for your OS',
      devicesDownloadInstallerButton: 'Download installer',
      devicesDownloadInstallerWindowsX64: 'Windows x64',
      devicesDownloadInstallerWindowsARM: 'Windows ARM',
      devicesDownloadInstallerMacOS: 'MacOS',
    },
    cursorHooks: {
      title: 'Cursor Enterprise Hooks',
      preCreationInfo:
        'Click "Create" to generate this integration. Once created, you will be able to download pre-configured hook scripts for Cursor Enterprise.',
      description:
        "Download pre-configured hook scripts for Cursor Enterprise. Each script contains your enrollment key and Zenity endpoint configuration. Scripts are managed from this admin console and applied across your organization's Cursor deployments.",
      downloadAll: 'Download All Hooks',
      osSelection: 'Operating System',
      macLinux: 'Mac / Linux',
      windows: 'Windows',
    },
    inLineWebhookService: {
      title: 'In-line Webhook',
      description: 'Intercepts and enforces policies on prompts and responses in real time.',
      guideTitle: 'Copilot Studio connection guide',
      guideDescription: 'Follow the steps in the guide below to configure your integration in CPS.',
      pdfLinkText: 'Enable Zenity Inline Threat Detection and Prevention for Copilot Studio Agents',
      saveButton: 'SAVE WEBHOOK',
      removeButton: 'REMOVE WEBHOOK',
      errorMessageOnUpdateInlineIntegration: 'Something went wrong - could not update inline webhook',
      errorMessageOnRemoveInlineIntegration: 'Something went wrong - could not remove inline webhook',
    },
    foundryService: {
      title: 'Microsoft Foundry',
      description: 'Configure your Microsoft Foundry integration',
      guideTitle: 'Microsoft Foundry connection guide',
      guideDescription: 'Follow the steps in the guide below to configure your integration in Microsoft Foundry.',
      pdfLinkText: 'Enable Zenity Inline Threat Detection and Prevention for Microsoft Foundry',
      applicationIdLabel: 'Application ID',
      apiKeyRequiredNotice: 'To add a Foundry Inline integration, generating an API key is required.',
      apiKeyLabel: 'API Key',
      generateApiKeyButton: 'Generate API Key',
      revokeApiKeyButton: 'Revoke API Key',
      apiKeyCopiedMessage: 'API key copied to clipboard',
      webhookSavedMessage: 'In-line webhook saved successfully',
      apiKeyGeneratedMessage: 'API key generated successfully',
      apiKeyRevokedMessage: 'API key revoked successfully',
      errorGeneratingApiKey: 'Failed to generate API key',
      errorRevokingApiKey: 'Failed to revoke API key',
      saveButton: 'SAVE WEBHOOK',
      removeButton: 'REMOVE WEBHOOK',
    },
    removeWebhookConfirmation: {
      title: 'Remove webhook',
      content: 'Are you sure you want to remove this webhook?',
    },
    removeAllInlineWebhooksConfirmation: {
      title: 'Disable In-line Webhook',
      content:
        'Are you sure you want to disable the In-line Webhook? All existing integrations (Copilot Studio, Foundry) and API keys will be removed.',
    },
    removeCopilotStudioConfirmation: {
      title: 'Remove Copilot Studio integration',
      content: 'Are you sure you want to remove the Copilot Studio integration? This action cannot be undone.',
    },
    removeFoundryConfirmation: {
      title: 'Remove Foundry integration',
      content: 'Are you sure you want to remove the Foundry integration? Any existing API keys will be revoked.',
    },
    inlineWebhookDisabledPreviewText: 'In-line Webhook will be available after connector is created',
    copilotStudioSectionTitle: 'Copilot Studio',
    copilotStudioDescription: 'Configure your Copilot Studio integration',
    foundryServiceSectionTitle: 'Microsoft Foundry',
    tenantIdLabel: 'Tenant ID',
  },
  policyPage: {
    config: {
      generalTab: 'General',
      trustedDomainsTab: 'Trusted Domains',
      connectorsTab: 'Connectors',
      environmentsTab: 'Environments',
      endpointFilteringTab: 'Endpoint Filtering',
      mcpServerFilteringTab: 'Allowed MCP Servers',
      sensitiveLabelsTab: 'Sensitive Labels',
      sensitiveLocationsTab: 'Sensitive Locations',
      settings: 'Settings',
      connectorsContent: {
        description:
          "Connectors are API wrappers meant to allow data access from the source platform to 3rd parties.\nZenity categorizes these connectors to be later used in our Data Leak Prevention policies.\nA simple example would be to flag any movement from 'Social-Media' to a 'High-Risk' Connector, that the Zenity user can define.",
      },
      trustedDomainsContent: {
        allowedDomains: 'Allowed Domains',
        allowedDomainsDesc:
          'Describes the Organization domain.\n Zenity uses the email domain in our rule engine to detect various scenarios such as: Data Leak or if an external user has access to your platform and more.',
        domainsList: 'Domains List',
        addDomain: 'Add Domain',
        newDomain: 'New Domain',
        addMore: 'Add More',
        enterDomain: 'Enter Domain...',
        deleteDomain: 'Delete Domain',
      },
      endpointFilteringContent: {
        allowedEndpoints: 'Allowed Endpoints',
        allowedEndpointsDesc:
          "Lists the organization's allowed endpoints.\n Zenity uses these endpoints in our rule engine to detect if a resource is communicating with an endpoint outside of the allowed list.",
        addEndpoint: 'Add Endpoint',
        addMore: 'Add More',
        enterEndpoint: 'Enter endpoint...',
        deleteEndpoint: 'Delete Endpoint',
        protocol: 'Protocol',
        addEndpointsDescription:
          "Enter the desired endpoint URL, which may include wildcards at the end of the path (e.g 'http://example.com/path/*').",
        invalidEndpoint:
          "The endpoint must consist of a valid domain and optionally a path and a protocol. e.g 'http://example.com/path'",
        enterUniqueEndpointsMsg: 'Please enter unique endpoints',
      },
      mcpServerFilteringContent: {
        allowedMcpServers: 'Allowed MCP Servers',
        allowedMcpServersDesc:
          "Lists the organization's allowed MCP servers.\n Zenity uses these servers in our rule engine to detect if a resource is communicating with an MCP server outside of the allowed list.",
        addMcpServer: 'Add MCP Server',
        addMore: 'Add More',
        enterMcpServer: 'Enter MCP server URL, Docker image path, or package path...',
        deleteMcpServer: 'Delete MCP Server',
        addMcpServersDescription:
          'Enter the desired MCP server URL, Docker image path, or package path. You can specify multiple fields for better identification.',
        invalidMcpServer: 'The MCP server must be a valid URL, Docker image path, or package path.',
        enterUniqueMcpServersMsg: 'Please enter unique MCP servers',
        addMcpServerDialogTitle: 'Add MCP Server',
        addMcpServerDialogDescription: "Enter the desired MCP server URL (e.g 'https://example.com/mcp').",
        enterMcpServerUrl: 'Enter MCP server...',
        invalidMcpServerUrl: 'Please enter a valid URL starting with http:// or https://',
        addMoreButton: 'Add More',
        cancelButton: 'Cancel',
        addButton: 'Add',
      },
      sensitiveLabelsContent: {
        description: 'Specify sensitivity labels to monitor for sensitive data access and generate alerts.',
        selectedCount: 'Selected',
        labelIdColumn: 'Label ID',
        labelNameColumn: 'Label Name',
        searchPlaceholder: 'Search labels...',
      },
      sensitiveLocationsContent: {
        description: 'Specify file locations to monitor for sensitive data access and generate alerts.',
        selectedCount: 'Selected',
        addLocation: 'Add Location',
        locationsList: 'Locations List',
        locationUrlColumn: 'Location URL',
        searchPlaceholder: 'Search...',
        addLocationDialogTitle: 'Add Location',
        enterLocation: 'Enter location URL',
        addMore: 'Add More',
        deleteLocation: 'Delete Location',
        urlRequiredError: 'Location URL is required.',
        urlInvalidDomainError:
          'Invalid domain. URL must be from SharePoint (.sharepoint.com) or OneDrive (-my.sharepoint.com)',
        urlInvalidPathError:
          'Incomplete path. SharePoint paths require /sites/{sitename} and OneDrive paths require /personal/{user}/Documents',
        urlDuplicateError: 'This location overlaps with an existing location in the sensitive locations list',
        locationLimitReachedMessage: 'Maximum of 15 locations can be added.',
      },
      generalSettingsContent: {
        policyName: 'Policy Name',
        policyNameDesc: 'You can change the policy name',
        policyType: 'Policy Type',
        allowedNumberOfBuilders: 'Allowed Number of Builders',
        allowedNumberOfBuildersDesc:
          'Zenity detects cases where the number of flow builders\n is greater than the allowed number of builders\n to minimize the risk for a potential business disruption.',
        highNumberOfPrivilegedUsers: 'High Number of Privileged Users in the Environment',
        highNumberOfPrivilegedUsersDesc:
          'Zenity detects cases where the number of privileged users in the environment\n is greater than the defined number of high privileged users threshold\nto minimize the risk for a potential business disruption.',
        criticalNumberOfPrivilegedUsers: 'Critical Number of Privileged Users in the Environment',
        criticalNumberOfPrivilegedUsersDesc:
          'Zenity detects cases where the number of privileged users in the environment\n is equal or greater than the defined number of critical privileged users threshold to minimize the risk for a potential business disruption.',
        allowedConnectionsToProd: 'Allow connections to Production',
        allowedConnectionsToProdDesc:
          'Zenity detects and alerts in case a non-production connection\n is used in a production environment',
        allowedConnectionsToNonProd: 'Allow connections to Non-Production environments',
        allowedConnectionsToNonProdDesc:
          'Zenity detects and alerts in case a production connection is used\n in a non-production environment',
        allowedIdentityProviders: 'Allowed Identity Providers for Power Pages',
        allowedIdentityProvidersDesc:
          'Zenity detects usage of identity providers which are not trusted by the organization',
        allowedNumOfPrivilegedIdentitiesInWorkspace: 'Allowed Number of Privileged Identities in Workspace',
        allowedNumOfPrivilegedIdentitiesInWorkspaceDesc:
          'Zenity detects cases where the total number of privileged identities (Contributor, Member, Admin) of a workspace is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToDashboard: 'Allowed Number of Privileged Identities to Dashboard',
        allowedNumOfPrivilegedIdentitiesToDashboardDesc:
          'Zenity detects cases where the total number of privileged identities (Reshare, Build) of a Dashboard is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToDataset: 'Allowed Number of Privileged Identities to Dataset',
        allowedNumOfPrivilegedIdentitiesToDatasetDesc:
          'Zenity detects cases where the total number of privileged identities (Reshare, Build) of a Dataset is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToReport: 'Allowed Number of Privileged Identities to Report',
        allowedNumOfPrivilegedIdentitiesToReportDesc:
          'Zenity detects cases where the total number of privileged identities (Reshare, Build) of a Report is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToAgent: 'Allowed Number of Privileged Identities to Agent',
        allowedNumOfPrivilegedIdentitiesToAgentDesc:
          'Zenity detects cases where the total number of privileged identities to an Agent is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToGpt: 'Allowed Number of Privileged Identities to GPT',
        allowedNumOfPrivilegedIdentitiesToGptDesc:
          'Zenity detects cases where the total number of privileged identities to GPT is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToTable: 'Allowed Number of Privileged Identities to Table',
        allowedNumOfPrivilegedIdentitiesToTableDesc:
          'Zenity detects cases where the total number of privileged identities to a table is greater than the defined number of privileged identities threshold.',
        allowedNumOfPrivilegedIdentitiesToAiSkill: 'Allowed Number of Privileged Identities to AI Skill',
        allowedNumOfPrivilegedIdentitiesToAiSkillDesc:
          'Zenity detects cases where the total number of privileged identities to AI Skill is greater than the defined number of privileged identities threshold.',
        allowedIdentityProvidersPlaceholder: 'Select Identity Providers...',
        allowedMemoryRetentionInDays: 'Number of Days to Retain Agent Memory (Conversational Context)',
        allowedMemoryRetentionInDaysDesc:
          'Zenity detects cases where the number of days that agent configured to retain memory (conversational context) is greater than the defined number of days to retain agent memory threshold.',
        allowedMemoryMaxRecentSessions: 'Maximum Number of Recent Sessions To Include in Memory Context',
        allowedMemoryMaxRecentSessionsDesc:
          'Zenity detects cases where the number of recent sessions that agent configured to include in memory context is greater than the defined number of recent sessions threshold.',
        effectivePolicySettingsTitle: 'Effected Rules',
        effectivePolicySettingsRuleId: 'Rule ID',
        effectivePolicySettingsRuleName: 'Rule Name',
      },
    },
    morePolicies: 'Custom policies',
    disabledAddPolicyReachedMaxMsg: "You've reached the maximum number of policies",
    disabledAddPolicyNoPermissionMsg: 'You do not have sufficient permissions to add a policy',
    addPolicy: 'Add policy',
    duplicatePolicy: 'Duplicate policy',
    enterPolicyName: 'Enter policy name',
    duplicationMessage: 'Duplicate will copy the entire policy configuration excluding the Environments association',
    deletePolicy: 'Delete Policy',
    noPoliciesFoundMsg: 'No policies found',
    policy: {
      violationRulesTab: 'Rules',
      hygieneRulesTab: 'Hygiene Rules',
      exemptionRulesTab: 'Exemption Rules',
      configurationTab: 'Configuration',
      inLineControlRulesTab: 'In-Line Control',
      violationRules: {
        groupName: 'Group name',
        groupBy: 'Group by',
        tags: 'Tags',
        status: 'Status',
        severity: 'Severity',
        aiService: 'AI Service',
        lightningSupport: 'Lightning Support',
        user: 'User',
        none: 'None',
        owasp: 'OWASP LCNC',
        mitre: 'MITRE ATT&CK',
        owaspLlm: 'OWASP LLM',
        owaspAsi: 'OWASP Agentic AI',
        owaspNhi: 'OWASP NHI',
        genAiMatrix: 'MITRE ATLAS (AI Agents Attack Matrix)',
        lightningTooltip: 'Near real-time updates powered by Zenity Lightning',
        tableColumns: {
          ruleName: 'Rule Name',
          ruleId: 'Rule ID',
          ruleTags: 'Rule Tags',
          violations: 'Count',
          category: 'Category',
          severity: 'Severity',
          user: 'User',
          status: 'Status',
          lightningSupport: 'Lightning Support',
        },
        dialogsLabels: {
          statusChangedTitle: 'Severity / Status change',
          resetTitle: 'Reset Rule',
          statusChangedMessageContent: 'Are you sure you want to change the configuration of this policy?',
          resetMessageContent: 'Are you sure you want to reset this rule?',
        },
      },
      environments: {
        title: 'Environments list',
        tableColumns: {
          environmentName: 'Environment name',
          createdBy: 'Created By',
          createdAt: 'Created At',
          resources: 'Resources',
          status: 'Status',
        },
        associatedWith: 'Associated with ',
        associationDescription: 'Remove 1st to associate with a new policy',
      },
      inLineControlRules: {
        tableColumns: {
          ruleName: 'Rule Name',
          ruleId: 'Rule ID',
          detections: 'Detections',
          severity: 'Severity',
          user: 'User',
          detection: 'Detection',
          prevention: 'Prevention',
          status: 'Status',
        },
      },
    },
  },
  enterpriseAiDetectionPage: {
    tabs: {
      activitySubtitle: `This page shows real-time AI activity within the organization, broken down into individual "steps." <br> Each step represents a user or AI action within an "AI thread", providing a transparent, detailed view of AI interactions and autonomous operations.`,
      activityTooltip: 'Zenity retains three months of your runtime data and activity.',
      findingsTooltip: 'Zenity retains three months of your runtime data and findings.',
      findingsSubtitle: 'Below are security findings based on real time digest of your AI activity.',
      inLineControl: 'actions',
      inLineControlTooltip: 'In-Line Control',
      inLineControlSubtitle: `Monitor real-time enforcement actions taken directly within AI workflows. <br>Backed by AI Edge and AIDR, this includes proactive blocking, behavior-based detection, and policy-driven control of agent activity as it happens.`,
    },
  },
  userManagementPage: {
    tabs: {
      users: 'Users',
      accessGroups: 'Access Groups',
      api: 'API',
      auditLogs: 'Audit Logs',
    },
    usersPage: {
      title: 'Users',
      inviteUser: 'Invite user',
      tableColumns: {
        name: 'User name',
        email: 'Email',
        createdAt: 'Created at',
        lastLogin: 'Last login',
        memberOfGroups: 'Member of access group',
        role: 'Role',
      },
      actions: {
        edit: 'Edit',
        delete: 'Delete',
        copyInvitationUrl: 'Copy invitation URL',
      },
      deleteUserTitle: 'Delete user',
      roleModificationTitle: 'Role Modification',
      roleModificationBody: "Are you sure you want to modify user's role?",
      inviteUserTitle: 'Invite user',
      deleteUsersMultiActionName: 'Delete Users',
      inviteUserEmailTitle: 'User email',
      inviteUserEmailPlaceholder: 'Enter an email',
      inviteUserRoleTitle: 'User role',
      inviteUserRolePlaceholder: 'Select a role...',
      authenticationTitle: 'Authentication',
      authenticationPlaceholder: 'Select an authentication...',
      userIsAdminLabel: 'User is admin',
      userIsAdminDescription: 'Admins have access to all features and settings.',
      assignToAccessGroupLabel: 'Assign user to access group',
      selectAccessGroupsPlaceholder: 'Select one or more access groups',
    },
    apisPage: {
      clientIdTitle: 'Client ID',
      clientIdDescription: 'The Zenity cross-platform account id, used when authenticating with the Zenity API',
      clientSecretTitle: 'Client Secret',
      clientSecretRotation: 'Rotate Secret',
      clientSecretDescription:
        "The Zenity API secret, used together with the 'Client ID' when authenticating with the Zenity API",
      notFoundText: 'No API credentials for this Account',
      areYouSureTitle: 'Client Secret Rotation',
      areYouSureBody: 'Are you sure you want to rotate your client secret?',
      secretRotationSucceeded: 'Client secret rotation succeeded',
    },
    auditLogsPage: {
      title: 'Audit logs',
      filters: {
        search: 'Search in event details',
      },
      tableColumns: {
        timestamp: 'Timestamp',
        objectType: 'Object type',
        objectId: 'Object ID',
        action: 'Action',
        actorType: 'Actor type',
        actorEmail: 'Actor email',
        details: 'Details',
      },
      auditLogEventDetailsDialog: {
        title: 'Event details',
        close: 'Close',
        eventInfo: 'Event Information',
        actorInfo: 'Actor Information',
        metadata: 'Metadata',
        entityId: 'Entity Id:',
        summary: {
          objectType: 'Object type:',
          action: 'Action:',
          actorEmail: 'Actor email:',
        },
        fields: {
          id: 'ID',
          timestamp: 'Timestamp',
          entityType: 'Entity Type',
          entityAction: 'Entity Action',
          email: 'Email',
          source: 'Source',
        },
      },
    },
    accessGroupsPage: {
      title: 'Access Groups',
      filters: {
        search: 'Search by group name',
      },
      actions: {
        edit: 'Edit',
        delete: 'Delete',
      },
      createAccessGroup: 'Create access group',
      tableColumns: {
        groupName: 'Access group name',
        members: '# Members',
        createdBy: 'Last modified by',
        createdAt: 'Created at',
        lastChanged: 'Last changed',
        users: 'Users',
        role: 'Role',
        integrations: 'Integrations',
      },
    },
  },

  integrationStatus: {
    activeStatus: 'Active',
    disabledStatus: 'Disabled',
    inactiveStatus: 'Inactive',
  },
  policiesPage: {
    title: 'Policies',
    selectIntegrationPlaceHolder: 'Select integration...',
    nameCol: 'Policy Name',
    idCol: 'Policy ID',
    severityCol: 'Severity',
    platformCol: 'Platform',
    environmentCol: 'Environment',
    violationsCol: 'Violations',
    statusCol: 'Status',
    toggleConfirmationTitle: 'Status change',
    toggleConfirmationContent: 'Are you sure you want to change the status of these policies?',
    updatingButtonText: 'Updating...',
    updateButtonText: 'Update',
    deleteButtonTextHoverText: 'Deleting is disabled, the list must have at least one endpoint',
  },
  issuesPage: {
    name: 'Issues',
    allIssuesTab: 'All issues',
    incidentsTab: 'Incidents',
    exposuresTab: 'Exposures',
    filters: {
      search: 'Search by issue or resource name or ID',
    },
    gridColumns: {
      name: 'Issue',
      severity: 'Severity',
      user: 'User',
      primaryResource: 'Primary Resource',
      correlations: 'Correlations',
      firstSeen: 'First Seen',
      lastSeen: 'Last Seen',
      labels: 'Labels',
      status: 'Status',
    },
    issueOverviewTab: 'Overview',
    issueRemediationTab: 'Remediation',
    idTitle: 'Id:',
    issueAnalysis: 'Issue Analysis',
    resourceRelationsGraph: 'Graph',
    issueTimeLine: 'Timeline',
    timelineIssueCitations: 'Citations:',
    relatedFinding: {
      description: 'Findings',
      type: 'Type',
      timestamp: 'Timestamp',
      severity: 'Severity',
      user: 'User',
      link: 'Link',
      tags: 'Tags',
    },
    fetchTimelineMessagesContentButton: 'Fetch Messages Content',
    actionDialogLabels: {
      fileName: 'File Name',
      fileSize: 'File Size',
      scanResults: 'Scan Results',
      uploadDate: 'Upload Date',
    },
    quarantineUserDialog: {
      tableColumns: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
      },
      executeActionsButton: 'Execute actions',
    },
  },
  issueActions: {
    resolve: 'Resolve Issue',
    reopen: 'Reopen Issue',
  },
  issuePage: {
    name: 'Findings/Violations',
    gridColumns: {
      name: 'Name',
      type: 'Type',
      severity: 'Severity',
      user: 'User',
      ruleTags: 'Rule Tags',
      timestamp: 'Timestamp',
    },
  },
  hygienePage: {
    title: 'violations',
    gridColumns: {
      violationName: 'Violation Name',
      labels: 'Labels',
      resourceName: 'Resource Name',
      owasp: 'OWASP',
      policyId: 'Policy ID',
      resourceType: 'Type',
      createdBy: 'Created By',
      ownedBy: 'Owned By',
      ruleTags: 'Rule Tags',
      platform: 'Platform',
      resourceStatus: 'Resource Status',
      severity: 'Severity',
      user: 'User',
      firstSeen: 'First Seen',
      violationStatus: 'Violation Status',
    },
    filters: {
      search: 'Search by Rule Name, Rule ID, Resource Name, or Platform Resource ID',
    },
  },
  violationsPage: {
    title: 'Violations',
    gridColumns: {
      violationName: 'Violation Name',
      labels: 'Labels',
      resourceName: 'Resource Name',
      owasp: 'OWASP',
      policyId: 'Policy ID',
      resourceType: 'Type',
      createdBy: 'Created By',
      ownedBy: 'Owned By',
      ruleTags: 'Rule Tags',
      platform: 'Platform',
      resourceStatus: 'Resource Status',
      severity: 'Severity',
      user: 'User',
      firstSeen: 'First Seen',
      violationStatus: 'Violation Status',
    },
    checkboxes: {
      showExempted: 'Show exempted',
    },
    filters: {
      search: 'Search by Rule Name, Rule ID, Resource Name, or Platform Resource ID',
    },
  },
  supportPage: {
    tickets: {
      new_ticket_title: 'New Ticket',
      title: 'Title',
      description: 'Description',
      type: 'Type',
      severity: 'Severity',
      user: 'User',
      phone_number: 'Phone Number',
      uploadFileWarning: `Please do not upload files containing sensitive data. Limited to ${MaxNumberOfSupportFiles} files, each file size is limited to maximum ${MaxSupportFileSizeInMB} MB. Supported file formats: png, jpeg, jpg, txt, json.`,
      successMessage: 'Support ticket was created. An email was sent to your mailbox.',
      criticalTicketWarning: `I hereby acknowledge the following. Opening a critical ticket requires 24x7 availability in the provided credentials till ticket resolution or severity change. In case we will not be able to reach you the severity of the ticket will be changed to Major.`,
      severityLabels: {
        low: 'Low - Small product issue or a question',
        minor: 'Minor - General support issues',
        major: 'Major - Production related issue',
        critical: 'Critical - Major Production feature does not work',
      },
    },
  },
  violationActions: {
    exempt: 'Exempt',
    waive: 'Waive',
  },
  AiDataInsightsActions: {
    exclude: 'Exclude from RAG',
    include: 'Include in RAG',
  },
  commonActions: {
    sendEmail: 'Send Email',
    delete: 'Delete',
    deleteResource: 'Delete Resource',
    isolate: 'Isolate',
    addOwner: 'Add Co-Owner',
    removeOwner: 'Remove Co-Owner',
    setAccess: 'Set Access',
    disable: 'Disable',
    enable: 'Enable',
    getFlowDefinition: 'Get Raw Resource',
    getResourceSbomFile: 'Generate SBOM',
    view: 'View',
    agentActionEnforceUserConfirmation: 'Enforce End-User Confirmation',
    agentActionEnforceUserAuthentication: 'Enforce End-User Authentication',
    columns: 'Columns',
    sendEmailAction: {
      contactMaker: 'Contact Maker',
      contactActor: 'Contact Actor',
      to: 'To',
      cc: 'Cc',
      subject: 'Subject',
      body: 'Body',
      contactMakerDisabledTooltip: 'Maker is not found',
      contactActorDisabledTooltip: 'Actor is not found',
    },
    quarantine: 'Quarantine',
    unquarantine: 'Unquarantine',
    sendWebhookAction: {
      url: 'URL',
      token: 'Secret',
      secretHeaders: 'Secret Headers',
      secretHeadersInfo: 'Values are encrypted and stored securely',
      customHeaders: 'Custom Headers',
      headerName: 'Name',
      headerValue: 'Value',
      addHeader: 'Add Header',
    },
    deleteAgentThread: 'Delete Thread',
    sendToSoc: 'Send to SOC',
    addOwnerAction: {
      disclaimer: 'Changes will be applied immediately and be reflected in Zenity within 24h',
      newOwnerEmail: 'New Co-Owner Email',
      openPlatformOnSuccess: 'Open Platform On Success',
    },
    addLabelAction: {
      chooseLabelTitle: 'Add Labels',
      writeDescriptionTitle: 'Description (optional)',
    },
    removeOwnerAction: {
      ownerEmailToRemove: 'Co-Owner Email To Remove',
    },
    secureFlowStepsAction: {
      title: 'Secure Flow Steps',
      supportedRulesHeader: 'Supported Rules',
      limitationsHeader: 'Limitations',
      limitationsDisclaimer:
        "The action supports 'Unmanaged Solution Aware' flows, meaning they are in a solution that is not managed. This ensures the integrity and stability of managed solutions is maintained. Due to a limitation in the PowerPlatform API, this is not yet possible for non-solution aware flows.",
      actionOverviewHeader: 'Action Overview',
      actionOverviewTitle: 'This action automatically addresses a critical security concern:',
      actionOverviewDescription: {
        sensitiveData:
          'Power Automate flows that log sensitive data in plain text, potentially exposing it to unauthorized users. Zenity identifies and secures these steps by enabling data masking, ensuring the information is encrypted and protected.',
      },
      howItWorksHeader: 'How It Works:',
      howItWorksDescription:
        'The action is triggered by specific violation rules that detect risky flows. It targets only the flow steps identified as risky, preserving the ability to debug the rest of the flow as needed.',
    },
    manageAccessAction: {
      manageAccess: 'Set Access',
      disclaimer: 'Changes will be applied immediately and be reflected in Zenity within 24h',
      openPlatform: 'Open Platform',
      fetchingPermissionsFromPlatform: 'Fetching Permissions From Platform',
      revokeAccessSelected: 'Remove Access',
      isolateResourceButtonLabel: 'Isolate Resource',
      isolateResourceButtonTooltip: 'Removes access permissions for this resource to all users, except the owner.',
      setAccessTitle: 'Set Access',
      searchPlaceholder: 'Search by name or email',
      selectAll: 'Select all',
      users: 'users',
      changeRole: 'Change role',
      updatingProgress: 'Updating',
      noUsersFound: 'No users found matching your search',
      noUsersWithAccess: 'No users have access to this resource',
      isolateResource: 'Isolate Resource',
      isolateResourceTooltip: 'Remove access for all users except owners',
      close: 'Close',
      setAllUsersRole: 'Set all users role',
      selectRole: 'Select role',
      isolateResourceConfirmTitle: 'Isolate Resource',
      isolateResourceConfirmContent:
        'Are you sure you want to isolate this resource? This will remove access for all users except owners.',
      isolateResourceConfirmYes: 'Yes, Isolate',
      isolateResourceConfirmNo: 'Cancel',
      bulkRoleChangeConfirmTitle: 'Change All Users Role',
      bulkRoleChangeConfirmContent:
        'Are you sure you want to change the role for all users? This action will affect all users with access to this resource.',
      bulkRoleChangeConfirmYes: 'Yes, Change Role',
      bulkRoleChangeConfirmNo: 'Cancel',
      resourceNotFoundError:
        'The resource no longer exists or insufficient permissions to access it. Please verify that Zenity has admin permissions for this workspace.',
      addUser: 'Add user',
      addUserEmailPlaceholder: 'Provide email',
    },
    enforceUserAuthenticationAction: {
      title: 'Enforce End-User Authentication',
      content: "Zenity will modify the agent's action configuration to enforce end-user authentication",
    },
    enforceUserConfirmationAction: {
      title: 'Enforce End-User Authentication',
      content:
        "Zenity will modify the agent's action configuration to enforce end-user confirmation before action execution",
    },
    download: {
      title: 'Download',
      successMessage: 'Download completed successfully',
      errorMessage: 'Download failed',
    },
    isolateGptAction: {
      title: 'Isolate GPT',
      content: 'Zenity will remove access permissions for this GPT to all users, except the owner.',
    },
    deleteGptCanvas: {
      title: 'Delete Canvas',
      content: "Deletion of a user's canvas.",
    },
    getGptCanvas: {
      title: 'Get Canvas',
      content: "Deletion of a user's canvas.",
    },
    getGptKnowledge: {
      title: 'Download',
      content: 'Download the selected knowledge file from the GPT.',
    },
    deleteGptConversationAction: {
      title: 'Delete Thread',
      content:
        "This action will permanently delete the entire thread (conversation) from ChatGPT.<br><br>Note: The thread will still be available through OpenAI's Compliance API and accessible in Zenity for compliance purposes.",
    },
    manageSecureFlowStepsSolutionAwareAction: {
      title: 'Confirmation',
      content:
        "Zenity is going to mask the sensitive data found, by enabling the 'Secure Input/output' setting on the relevant flow steps",
    },
    waitAction: {
      waitTime: 'Wait Time',
    },
    stop: 'Stop',
    start: 'Start',
    killAgent: 'Kill Agent',
    killAgentAction: {
      title: 'Kill Agent',
      content:
        'Are you sure you want to kill this agent? The agent will be blocked and will no longer be able to execute any actions.',
    },
    killswitch: 'Killswitch',
    killswitchAction: {
      title: 'Killswitch',
      content:
        'Are you sure you want to contain this agent? The agent will be blocked and will no longer be able to execute any actions.',
    },
    secureFlowSteps: 'Secure Flow Steps',
    adminActionTooltip: 'This action is allowed for Admin only.',
    operatorActionTooltip: 'This action requires Operator or Admin roles.',
    disabledResourceActionTooltip: 'Supports only active flows.',
    deletedResourceTooltip: 'This resource was deleted.',
    getResourceDefinitionAction: {
      inactiveIntegrationMsg: 'The integration must be active to perform this action.',
      sensitiveDataDisclaimerPopupTitle: 'Confirmation',
      sensitiveDataDisclaimer:
        'The resource may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the resource is retrieved in real-time for display purposes only.',
    },
    getFlowDefinitionAction: {
      rawJsonDescription:
        'Use the raw JSON of the flow to investigate every aspect of the flow without going to the source platform.',
      title: 'Flow Raw Resource',
    },
    getRawResourceAction: {
      actionName: 'Get Raw Resource',
      getRawResourceSpecificResourceTypeText: {
        rawJsonDescription:
          'Use the raw JSON of the {0} resource to investigate every aspect of the {0} without going to the source platform.',
        title: '{0} Raw Resource',
      },
    },
    powerplatformSetCopilotAuthentication: {
      actionName: 'Set Authentication',
      title: 'Available Authentication Methods',
      noAuthentication: 'No Authentication',
      noAuthenticationDescription: 'Allows anonymous users (anyone) to communicate with the Agent (risky)',
      permittedUsersOnly: 'Only for Teams and PowerApps',
      permittedUsersOnlyDescription: 'Enforces AAD authentication & restricts org-wide access (preferred)',
      signinRequiredCustomWebsite: 'Requires users to sign in (Manual authentication only)',
      signinRequiredCustomWebsiteDescription:
        'Custom authentication, enforces user sign in & restricts org-wide access (preferred)',
      applyButton: 'Apply',
      disclaimer1: "Changes will be applied based on the Agent 'Published status'.",
      disclaimer2: 'Published Agent will be published again to enforce changes.',
      disclaimer3: 'Unpublished Agents will not be published.',
    },
    removePermissionsPlaybookAction: {
      title: 'Remove Permissions',
      orgWideAccess: 'Org-Wide Access',
      disclaimer: 'Removes tenant-wide access from an agent',
    },
    isolateResourcePlaybookAction: {
      description: 'This action removes access to this resource for all users except for the resource admins.',
    },
    setAllUsersAccessPowerBiWorkspace: {
      description: 'This action sets the access level for all users in the workspace except for the resource admins.',
      title: 'Select Access Level',
      admin: 'Admin (Owner)',
      adminDescription: 'Full control over the workspace including managing members and settings',
      member: 'Member (Can Edit)',
      memberDescription: 'Can edit content and publish reports in the workspace',
      contributor: 'Contributor (Can Edit)',
      contributorDescription: 'Can create, edit, and delete content in the workspace',
      viewer: 'Viewer (Read)',
      viewerDescription: 'Can only view content in the workspace',
    },
    removeAccessPlaybookAction: {
      title: 'Remove Access',
      supportedRulesAndLimitationsHeader: 'Supported Rules',
      supportedRulesHeader: 'Supported Rules',
      limitationsHeader: 'Limitations',
      limitationsDisclaimer:
        'In case you are trying to remove access for a guest user who is also the primary owner of the resource, that is currently not supported in the PowerPlatform API.',
      actionOverviewHeader: 'Action Overview',
      actionOverviewTitle: 'This action automatically addresses two primary security concerns:',
      actionOverviewDescription: {
        guestAccess:
          "Guest Access : Zenity identifies and removes guest user access, except in the case specified under the 'Limitations' section.",
        tenantWideAccess:
          'Tenant-Wide Access: Zenity identifies & eliminates access to resources that are shared across the entire tenant.',
      },
      howItWorksHeader: 'How It Works:',
      howItWorksDescription:
        'The action is triggered by specific rules that you configure. Once triggered, it assesses the type of access violation—either guest access or tenant-wide access and removes it accordingly. For detailed rules and scenarios, refer to the section below.',
    },
    getSbomFileAction: {
      noSbomAttachmentMsg: 'Currently supported for Canvas Apps with PCF components',
    },
    getFlowRunsAction: {
      displayName: 'Get Latest Flow Runs',
      dialogTitle: 'Flow Runs (Trigger inputs and outputs only)',
      dialogDescription: 'Use the flow runs to understand what the flow does without going to the source platform.',
      confirmationPopupTitle: 'Confirmation',
      confirmationPopupContent:
        'The flow runs may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, <b>the flow trigger inputs and outputs</b> are retrieved in real-time for display purposes only.',
    },
    issueActions: {
      setAgentActionAuth: 'Set Agent Action Auth',
      quarantineUser: 'Quarantine User',
      contactDataOwner: 'Contact Data Owner',
      routeToSOC: 'Route to SOC',
      excludeFromRAG: 'Exclude from RAG',
      restrictTriggerDomains: 'Restrict Trigger Domains',
      contactOwner: 'Contact Owner',
      disableAIAgentAction: 'Disable AI Agent Action',
      deleteConnection: 'Delete Connection',
      maskCredentials: 'Mask Credentials',
      removeAccess: 'Remove Access',
      disableMemories: 'Disable Memories',
    },
    chatGptEnterpriseDeleteConversationAction: 'Delete Thread',
    deleteAction: {
      confirmationText: 'Permanent delete',
    },
    actionStatus: {
      label: 'Set Action Status',
      description: 'Available Options',
      enableLabel: 'Enable',
      enableDescription: "Zenity will modify the agent's action status to 'Enabled'",
      disableLabel: 'Disable',
      disableDescription: "Zenity will modify the agent's action status to 'Disabled'",
    },
    authenticationMode: {
      label: 'Set Authentication Context',
      description: 'Available Options',
      userContextTitle: 'User Context (Preferred)',
      userContextDescription:
        "Enforces the user's identity who invoked the action, protecting against credentials sharing / user impersonation",
      authorContextTitle: 'Author Context (Hazardous)',
      authorContextDescription:
        "Enforces the author's credentials potentially resulting in credentials sharing / user impersonation",
      ignoreIfAutonomousWithTriggersTitle: 'Exclude Autonomous Agents with Triggers',
      ignoreIfAutonomousWithTriggersDescription:
        'When enabled, this action will be excluded if the agent is autonomous and has triggers configured',
    },
    confirmationMode: {
      label: 'Set Confirmation Mode',
      description: 'Available Options',
      strictModeTitle: 'Request User Confirmation',
      strictModeDescription:
        "Zenity will modify the agent's action configuration to enforce end-user confirmation before executing the action",
      disabledModeTitle: 'Skip User Confirmation',
      disabledModeDescription:
        "Zenity will modify the agent's action configuration not to enforce end-user confirmation before executing the action",
      ignoreIfAutonomousWithTriggersTitle: 'Exclude Autonomous Agents with Triggers',
      ignoreIfAutonomousWithTriggersDescription:
        'When enabled, this action will be excluded if the agent is autonomous and has triggers configured',
    },
    webBrowsing: {
      label: 'Set Web Browsing',
      description: 'Available Options',
      enableLabel: 'Enable Web Browsing',
      enableDescription: "Zenity will modify the agent's configuration to enable Web-Browsing",
      disableLabel: 'Disable Web Browsing',
      disableDescription: "Zenity will modify the agent's configuration to disable Web-Browsing",
    },
    environmentAudit: {
      label: 'Set Environment Audit',
      description: 'Available Options',
      enableLabel: 'Enable Audit',
      enableDescription: 'Zenity will enable auditing for this Copilot Studio environment',
      disableLabel: 'Disable Audit',
      disableDescription: 'Zenity will disable auditing for this Copilot Studio environment',
    },
  },
  errors: {
    401: '401 - Unauthorized error',
    403: '403 - Forbidden',
    404: '404 - Not found',
    422: '422 - Unprocessable Entity error',
    generic:
      'An unknown error has occurred, we have notified our engineers. Please try again and if the problem persists, reach out to support@zenity.io.',
    inventory404ResourceMightBeDeletedError:
      '404 - The resource was not found. It might have been deleted from the platform.',
  },
  labelsDialog: {
    setLabelsTitle: 'Set Labels',
    searchLabels: 'Search...',
    systemLabelsTitle: 'System Labels',
    noSystemLabelsMsg: 'No system labels defined',
    customLabelsTitle: 'Custom Labels',
    noCustomLabelsMsg: 'No custom labels defined, create new ones',
    createNewLabel: 'Create a new label',
    justificationTitle: 'Description',
  },
  createLabelDialog: {
    createLabelTitle: 'Create a New Label',
    newLabelNameTitle: 'Name',
    newLabelColorTitle: 'Select a Color',
    createButtonText: 'Create',
  },
  editLabelDialog: {
    editLabelTitle: 'Edit Label',
    newLabelNameTitle: 'Name',
    newLabelColorTitle: 'Select a Color',
    deleteButtonText: 'Delete',
    editButtonText: 'Save',
  },
  deleteLabelDialog: {
    deleteLabelTitle: 'Delete Label',
    deleteLabelContent:
      'This will remove this label. You should remove it from all violations labeled with it before deleting it.',
    deleteLabelButtonText: 'Delete Label',
  },
  violationPage: {
    violationStatusTitle: 'Violation Status:',
    firstSeenTitle: 'First seen:',
    lastSeenTitle: 'Last seen:',
    lastSeenTooltip: "Shows when Zenity last verified this resource\'s risk state",
    lightningTooltip: 'Near real-time updates powered by Zenity Lightning',
    idTitle: 'ID:',
    labels: 'Set Labels',

    violationTabTitle: 'Violation',
    notesTabTitle: 'Notes',
    auditsTabTitle: 'Audit Events',
    addJustificationButton: 'Add Justification',
    addNoteButton: 'Add Note',
    deleteNoteButton: 'Delete',
    deleteNoteConfirmationTitle: 'Delete note',
    deleteNoteConfirmationContent: "Are you sure?<br>This action can't be undone.",
    noteTextAreaPlaceholder: 'Type a note...',

    summaryCardTitle: 'What happened?',
    summaryCardFieldResourceName: 'Resource Name',
    summaryCardFieldResourceCategoryAndType: 'Category & Type',
    summaryCardFieldResourceStatus: 'Resource Status',
    summaryCardFieldPlatformResourceId: 'Platform Resource ID',
    summaryCardFieldCreated: 'Created',
    summaryCardFieldLastUsed: 'Last Used',
    summaryCardFieldLastModified: 'Last Modified',
    summaryCardFieldEnvironment: 'Environment',
    summaryCardFieldResourceCreatedBy: 'Resource Created By',
    summaryCardFieldResourceOwnedBy: 'Resource Owned By',
    summaryCardFieldAiService: 'AI Service',
    summaryCardFieldRuleId: 'Rule Id',
    summaryCardExploreResourcePlatformLink: 'Explore in platform',
    summaryCardExploreResourceInventoryLink: 'Explore in Inventory',
    summaryCardExploreResourceInventoryLinkDisabled:
      'Resource is not in the inventory because it was deleted from the platform.',

    graphCardTitle: 'Graph',
    graphMessageResourceDeleted: 'Graph is not available for deleted resources.',

    riskReasonCardTitle: 'Why is this a risk?',
    riskReasonCardCategoryFieldKey: 'Category',
    riskReasonCardAttackFieldKey: 'AI Agents Attack Matrix',
    riskReasonCardLlmOwaspFieldKey: 'OWASP LLM',
    riskReasonCardLcncOwaspFieldKey: 'OWASP LCNC',
    riskReasonCardAsiOwaspFieldKey: 'OWASP Agentic AI',
    riskReasonCardNhiOwaspFieldKey: 'OWASP NHI',

    triageSuggestionTitle: 'How to triage?',
    fixSuggestionTitle: 'How to fix?',
    nextStepsTitle: 'What should I do now?',

    findingsCardTitle: 'Findings',
    singlefindingCardTitle: 'Finding',
  },
  formErrors: {
    genericFormErrorMsg: 'Some fields are missing or contain invalid inputs',
    signInRequiredFormErrorMsg: 'Signing into platform is required',
    emailFormFieldMsg: 'Please enter a valid Email address',
    guidFormFieldMsg: 'Please enter a valid GUID',
    noSpaceFormFieldMsg: 'Please enter a valid value without spaces',
    requiredFormFieldMsg: "Field is required and can't be left empty",
    certificateThumbprintFormFieldMsg: 'Please enter a valid certificate thumbprint',
    certificatePrivateKeyFormFieldMsg:
      'Please enter a valid certificate private key, including start prefix and end suffix',
    salesforceInstanceUrlFormFieldMsg: 'Please enter a valid Salesforce instance URL',
    serviceNowInstanceUrlFormFieldMsg: 'Please enter a valid ServiceNow instance URL',
    chatGptEnterpriseApiKeyFormFieldMsg: 'Please enter a valid ChatGPT Enterprise API key',
    amazonBedrockIamRoleArnFormFieldMsg: 'Please enter a valid Amazon Bedrock IAM Role ARN',
    accountIdMismatchFormFieldMsg: 'Mismatch between the account ID and the IAM Role ARN account ID',
    airtablePersonalAccessTokenFormFieldMsg: 'Access Token must start with "pat" followed by alphanumeric characters',
    airtableTenantIdFormFieldMsg: 'Please enter a valid Airtable Enterprise Account ID',
    budibaseTenantIdFormFieldMsg: 'Please enter a valid tenant ID (alphanumeric characters and hyphens only)',
    budibaseCustomDomainFormFieldMsg: 'Please provide a valid URL for the custom domain',
    budibaseApiPathPrefixFormFieldMsg: 'Please enter a valid path prefix (must start with /)',
    budibaseConfigurationFormFieldMsg: 'Please select a hosting type and provide the required information',
    budibaseConfigurationFormFieldProvideBothMsg: 'Please select SaaS or Self-hosted option',
    budibaseConfigurationFormFieldProvideSingleMsg: "You can't provide both fields",
    budibaseConfigurationFieldLevelMsg: 'Please select either SaaS or Self-hosted option',
    selectSelfHostedFieldKey: 'Select Hosting Type',
    tenantIdOptionLabel: 'SaaS',
    tenantIdOptionDescription: 'Provide your Tenant ID for Budibase cloud instances',
    customDomainOptionLabel: 'Self-hosted',
    customDomainOptionDescription: 'Provide your custom domain for self-hosted Budibase instances',
    apiPathPrefixPlaceholder: '/proxy',
    apiPathPrefixDescription: 'Optional path prefix for reverse proxy configurations (e.g., /budibase)',
    mtlsClientCertificateFormFieldMsg: 'Please enter a valid certificate in PEM format (-----BEGIN CERTIFICATE-----)',
    mtlsClientCertificateDescription: 'Client certificate in PEM format for mTLS authentication',
    mtlsClientPrivateKeyDescription: 'Private key for the client certificate in PEM format',
    mtlsCaCertificateDescription: 'Optional. Provide the CA certificate if the gateway uses a private/self-signed CA',
  },
  common: {
    found: 'Found',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    on: 'On',
    off: 'Off',
    ok: 'OK',
    yes: 'Yes',
    no: 'No',
    true: 'True',
    false: 'False',
    add: 'Add',
    list: 'List',
    type: 'Type',
    select: 'Select',
    noThanks: 'No, Thanks',
    notAvailable: 'N/A',
    notApplicable: 'Not Applicable',
    yesDelete: 'YES, DELETE',
    duplicate: 'Duplicate',
    invite: 'Invite',
    by: 'by',
    at: 'at',
    insufficientData: 'Insufficient Data',
    comingSoon: 'Coming Soon',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    healthy: 'Healthy',
    safe: 'Safe zone',
    reset: 'Reset',
    mediumRisk: 'Medium risk',
    highRisk: 'High risk',
    lowRisk: 'Low risk',
    criticalRisk: 'Critical risk',
    export: 'Export CSV',
    violationsBulkTooltip:
      'Limited to 10,000 rows.\n Action will execute on the first results only, according to the sort order.',
    violationsExportToolTip:
      'Limited to 10,000 rows.\nOnly the first ones will be exported, according to the sort order.',
    inventoryExportToolTip:
      'Limited to 10,000 rows.\nOnly the first ones will be exported, according to the sort order.',
    name: 'Name',
    backTo: 'Back to',
    edit: 'Edit',
    noEditPermission: 'You do not have permission to edit',
    settings: 'Settings',
    properties: 'Properties',
    createdBy: 'Created By',
    ownedBy: 'Owned By',
    startTime: 'Start Time',
    endTime: 'End Time',
    log: 'Log',
    logs: 'Logs',
    areYouSureMessage: 'Are you sure you want to delete the following?',
    uploadFiles: 'Upload Files',
    uploadFile: 'Upload File',
    text_input_placeholder: 'Enter your text...',
    description_input_placeholder: 'Enter description',
    wasDeletedSuccessfully: 'was deleted successfully',
    tenantLevelResource: 'Tenant Level Resource',
    platformGenerated: 'Platform Generated',
    dueTo: 'due to',
    noData: 'No Data to Display',
    maximumItemsSelectionReached: 'maximum of {0} selected items is reached',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    noFileName: 'No file name',
  },
  identityTypes: {
    system: 'SYSTEM',
  },
  inventoryResourcePage: {
    statusTitle: 'Status:',
    idTitle: 'ID:',
    lightningTooltip: 'Near real-time updates powered by Zenity Lightning',
    lastRefreshedTitle: 'Last refreshed:',
    lastRefreshedTooltip: 'Shows when Zenity last refreshed this resource',

    overviewTabTitle: 'Overview',
    relationsTabTitle: 'Relations',
    violationsTabTitle: 'Violations',
    hygieneTabTitle: 'Hygiene',

    infoCardTitle: 'Info',
    infoCardGraphTitle: 'Graph',
    infoCardResourceCategoryFieldKey: 'Resource Category',
    infoCardResourceDescriptionFieldKey: 'Resource Description',
    infoCardInstructionsFieldKey: 'Instructions',
    infoCardWebBrowsingCapabilityFieldKey: 'Web Browsing Capability',
    infoCardResourceTypeFieldKey: 'Resource Type',
    infoCardPlatformResourceIdFieldKey: 'Platform Resource ID',
    infoCardCreatedByFieldKey: 'Created By',
    infoCardOwnedByFieldKey: 'Owned By',
    infoCardOrgWideAccessFieldKey: 'Org - Wide Accessible',
    infoCardPublicAccessFieldKey: 'Publicly Accessible',
    infoCardResourcePublicationStatusFieldKey: 'Publication Status',
    infoCardResourceVersionFieldKey: 'Version',
    infoCardUserAccessAmountFieldKey: 'Number of shared users',
    infoCardResourceBusinessCriticalityFieldKey: 'Business Criticality',
    infoCardResourceSecurityRiskFieldKey: 'Security Risk',

    infoCardAutomationTypeFieldKey: 'Automation Type',
    infoCardAutomationStatusFieldKey: 'Automation Status',
    infoCardAutomationLastRunStatusFieldKey: 'Last Run Status',
    infoCardAutomationStepsCountFieldKey: 'Steps Count',
    infoCardAutomationUsesAiModelFieldKey: 'Uses AI model',
    infoCardAutomationIsSolutionAwareFieldKey: 'Is Solution Aware',
    infoCardAutomationIsBotTriggerFieldKey: 'Is Agent Trigger',

    infoCardIdentityTypeFieldKey: 'Identity Type',
    infoCardIdentityProviderIdFieldKey: 'Identity Provider Id',
    infoCardIdentityEmailFieldKey: 'Email Address',
    infoCardIdentityDepartmentFieldKey: 'Department',
    infoCardIdentityIsActiveFieldKey: 'Is Active',
    infoCardIdentityIsExternalFieldKey: 'Is External',
    infoCardIdentityIsBuilderFieldKey: 'Is Builder',
    infoCardIdentityTotalMembersCountFieldKey: 'Total Members Count',

    infoCardConnectorProviderNameFieldKey: 'Provider Name',
    infoCardConnectorProviderCategoryFieldKey: 'Provider Category',
    infoCardConnectorVersionFieldKey: 'Version',
    infoCardConnectorBackendServiceUrlFieldKey: 'Backend Service URL',
    infoCardConnectorIsDeprecatedFieldKey: 'Is Deprecated',

    infoCardConnectionInUseFieldKey: 'Is Connection in Use',
    infoCardConnectionStatusFieldKey: 'Connection Status',
    infoCardConnectionAccountFieldKey: 'Connection Account',
    infoCardConnectionHostNameFieldKey: 'Connection Host Name',
    infoCardConnectionIsOnPremisesFieldKey: 'Is On Premises',
    infoCardConnectionAuthenticationMethodFieldKey: 'Authentication Method',
    infoCardConnectionConnectorLinkFieldKey: 'Connector',
    infoCardConnectionConnectorProviderNameFieldKey: 'Connector Provider Name',
    infoCardConnectionConnectorProviderCategoryFieldKey: 'Connector Provider Category',

    infoCardApplicationTypeFieldKey: 'Application Type',
    infoCardApplicationVersionFieldKey: 'Version',
    infoCardApplicationQuarantineStatusFieldKey: 'Quarantine Status',

    infoCardEnvironmentTypeFieldKey: 'Environment Type',

    infoCardDataStorageTypeFieldKey: 'Data Storage Type',
    infoCardDataStorageEntityNameFieldKey: 'Entity Name',

    infoCardBotTypeFieldKey: 'Agent Type',
    infoCardBotAutonomousTriggersCountFieldKey: 'Autonomous Triggers Count',

    filterBy: 'Filter by:',

    additionalInfoCardTitle: 'Additional Info',

    environmentCardTitle: 'Environment',
    environmentCardNameFieldKey: 'Name',
    environmentCardPlatformFieldKey: 'Platform',
    environmentCardTypeFieldKey: 'Type',
    environmentCardAiServiceFieldKey: 'AI Service',
    environmentPlatformLink: 'Explore in Platform',
    viewRuntimeActivity: 'View runtime activity',
    runtimeActivityDisabledNonProduction:
      'Runtime activity is not available for agents in Teams and Developer environments',

    generativeAiCardTitle: 'Gen-AI',
    generativeAiCardCreatedUsingGenerativeAiFieldKey: 'Created with Gen-AI',

    timelineCardTitle: 'Timeline',
    timelineCardCreatedEvent: 'Created',
    timelineCardLastModifiedEvent: 'Last modified',
    timelineCardLastPublishedEvent: 'Last published',
    timelineCardFirstSeenEvent: 'First seen by Zenity',
    timelineCardLastRunEvent: 'Last run',
    timelineCardSuspensionEvent: 'Suspended',
    timelineCardDlpLastEvaluated: 'Dlp last evaluated',
    timelineCardDatasetLastRefresh: 'Last refreshed at',
    timelineCardLastRefreshedEvent: 'Last refreshed',
    timelineCardLastRunNotFoundMessages: {
      noLastExecutionStatus: 'No data found',
      noDatafoundUsingFirstSeenAsAncorHover:
        'Data not found. This could be due to the action or process never having occurred, or the relevant data was deleted from the source platform',
      noDatafoundUsingFirstSeenAsAncorMessage: 'No data found',
      possiblyNeverOccuredMessage:
        'Possibly never occurred, or records were deleted before usage persistency began on December-2024',
    },

    resourcesRelationsCardsTitle: 'Resources Relations',
    resourcesRelationsCardShowingSampleDisclaimer: 'Presenting sample of relations per resource category',
    graphCategoryOverflowLink: 'View all in Inventory',
    relatedResourceCardStatusTitle: 'Status:',
    relatedResourceCardResourceCategoryFieldKey: 'Resource Category',
    relatedResourceCardResourceTypeFieldKey: 'Resource Type',
    relatedResourceCardResourceRelationTypeFieldKey: 'Relation Type',
    relatedResourceCardCreatedAtTitle: 'Created At',
    relatedResourceCardResourceCreatedByIdentityNameTitle: 'Created By',

    relatedViolationCardStatusTitle: 'Status:',
    relatedViolationCardExploreResourceLink: 'Explore ',
    brokenRelationDisclaimer: 'The related resource was not found.',
    contactSupport: 'Contact Support.',
    brokenRelationCardTitle: 'Unknown Resource',
    relatedViolationCardExploreViolationLink: 'Explore ',

    resourceViolationsCardsTitle: 'Violations',
    resourceHygieneCardsTitle: 'Hygiene',
    resourceViolationCardPolicyCategoryFieldKey: 'Policy Category',
    resourceViolationCardFirstSeenFieldKey: 'First seen',
    portalLinkDisabledTooltip: 'Portal app link is deprecated',
  },
  inventoryPage: {
    tableColumns: {
      resourceName: 'Resource Name',
      resourceCategory: 'Category',
      resourceType: 'Type',
      environment: 'Environment',
      createdBy: 'Created By',
      ownedBy: 'Owned By',
      platform: 'Platform',
      severity: 'Severity',
      user: 'User',
      creationTime: 'Creation Time',
      businessCriticality: 'Business Criticality',
      securityRisk: 'Security Risk',
      resourceStatus: 'Resource Status',
      privileged: 'Privileged',
      managed: 'Managed',
    },
    filters: { search: 'Search by Resource Name, Environment Name, or Platform Resource ID' },
  },
  evidenceListPage: {
    sensitiveContentEvidence: `Select "Fetch Content" to view details.`,
    attachedFiles: 'Attached Files',
    downloadTooltip: 'Download file',
    attachmentDownloadedSuccessfully: 'File download started',
    attachmentDownloadFailed: 'Failed to download file',
  },
  aiAgentStepActivityPage: {
    tableColumns: {
      actorName: 'Actor',
      timestamp: 'Timestamp',
      stepType: 'Type',
      threadId: 'Thread Id',
      agentName: 'Agent',
      clientApplication: 'Client Application',
    },
    filters: { search: 'Search by Actor Name' },
  },
  aiFindingsPage: {
    tableColumns: {
      actorName: 'Actor',
      timestamp: 'Timestamp',
      displayName: 'Finding',
      severity: 'Severity',
      user: 'User',
      labels: 'Labels',
      threadId: 'Thread Id',
      owasp: 'OWASP LLM',
      owaspAsi: 'OWASP Agentic AI',
      mitreAttack: 'AI Agents Attack Matrix',
      action: 'Action',
    },
    filters: {
      search: 'Search by finding or rule id',
      searchThreadId: 'Search by thread id',
    },
  },
  aiFindingsThreadsPage: {
    tableColumns: {
      name: 'Thread Name',
      actorName: 'Actor',
      agentName: 'Agent',
      findingsBySeverity: 'Findings',
      labels: 'Labels',
      firstActivity: 'First Activity',
      lastActivity: 'Last Activity',
      lastFinding: 'Last Finding',
      threadId: 'Thread Id',
      owasp: 'OWASP LLM',
      owaspAsi: 'OWASP Agentic AI',
    },
  },
  aiInLineControlPage: {
    runtimeControl: 'Inline Scenario',
    inLineFinding: 'In-line Finding',
    idTitle: 'ID:',
    summaryCardTitle: 'Summary',
    summaryDescription: 'Summary of the in-line control',
    viewRelatedControls: 'View Related Controls',
    viewThread: 'View Thread',
    ruleIdLabel: 'Rule ID',
    threadIdLabel: 'Thread ID',
    agentIdLabel: 'Agent ID',
    aiServiceLabel: 'AI Service',
    agentNameLabel: 'Agent',
    preventionTypeLabel: 'Prevention Type',
    preventionTypeBlocked: 'Blocked',
    preventionTypeDetected: 'Detected',
    timestampLabel: 'Timestamp',
    actorIdLabel: 'Actor ID',
    agentLabel: 'Agent',
    evidenceTitle: 'Evidence',
    evidenceNoData: 'No evidence available',
    evidenceFlowTitle: 'Flow',
    evidenceConnectorColumn: 'Service',
    evidenceToolColumn: 'Tool',
    evidenceLabelColumn: 'Label',
    evidenceTimestampColumn: 'Timestamp',
    owaspRiskKey: 'OWASP Risk',
    attackRiskKey: 'Attack Risk',
    tableColumns: {
      ruleName: 'Rule',
      ruleId: 'Rule ID',
      preventionType: 'Prevention type',
      timestamp: 'Timestamp',
      agentId: 'Agent ID',
      actor: 'Actor ID',
      threadId: 'Thread ID',
      environmentId: 'Environment ID',
    },
    subtitle: `Monitor real-time enforcement actions taken directly within AI workflows. <br>Backed by AI Edge and AIDR, this includes proactive blocking, behavior-based detection, and policy-driven control of agent activity as it happens.`,
  },
  aiDataInsightsPage: {
    tableColumns: {
      name: 'Name',
      fileUrl: 'URL',
      firstDetected: 'First Detected',
      lastDetected: 'Last Detected',
      evidence: 'Evidence',
      findingsCount: 'Finding Count',
    },
    subtitle:
      'Gain visibility into how AI interacts with your data: what it accesses, uses, or generates. monitor potential data exposure and take action to reduce risk.',
    sensitiveFiles: 'Sensitive Files',
    sensitiveFilesDescription:
      'Files flagged by Zenity threat intelligence as sensitive and requiring review to determine whether AI should have access to them.',
  },
  dataLensPage: {
    title: 'Data Lens',
    subtitle:
      'Gain visibility into how AI agents interact with your data: what they access, feed, or generate. Monitor data exposure and take action to reduce risk. <a href="https://docs.zenity.io/UserGuide/ZenityUG_AIDR_data_lens.html" target="_blank" rel="noopener noreferrer">Learn more</a>',
    tooltip:
      'Gain visibility into how AI agents interact with your data: what they access, feed, or generate. Monitor data exposure and take action to reduce risk.',
    filters: {
      search: 'Search data items',
    },
    tableColumns: {
      dataItem: 'Data Item',
      accessCount: 'Access Count',
      lastAccessed: 'Last Accessed',
      findings: 'Findings',
      sensitivity: 'Sensitivity',
    },
    detailsTabTitle: 'Details',
    activityTabTitle: 'Activity',
  },
  aiPreventionMockPage: {
    tableColumns: {
      name: 'Name',
      type: 'Type',
      size: 'Size',
      createdSentDate: 'Created/Sent At',
      ownerCreatedSentBy: 'Created/Sent By',
      status: 'Status',
      agent: 'Agent',
    },
  },
  aiPreventionDetailsPage: {
    idTitle: 'ID:',
  },
  aiAgentStepPage: {
    idTitle: 'ID:',
    overviewTabTitle: 'Overview',
    citedSourcesCardTitle: 'Citations',
    sourcesCardTitle: 'Other Sources',
    toolsCardTooltip:
      'Actions or tools, such as data analysis, email drafting, or meeting summarization, that were enabled or disabled during this step of the interaction.',
    actorsCardTitle: 'Actors',
    ClientCardTitle: 'Client',
    exploreThreadFindingsLinkName: 'Explore Thread Findings',
    stepDataSidePanelTitle: {
      userMessageStep: 'User Message Step',
      messageStep: 'Message Step',
      toolInvocationStep: 'Tool Invocation Step',
      toolInvocationRequestStep: 'Tool Call Request Step',
      toolInvocationResultStep: 'Tool Call Result Step',
      toolRequestStep: 'Tool Request Step',
      toolResponseStep: 'Tool Response Step',
      ragStep: 'RAG Step',
      aiMessageStep: 'AI Message Step',
      triggerStep: 'Trigger Step',
      memoryStep: 'Memory Step',
      agentHandoffRequestStep: 'Agent Handoff Request Step',
      agentHandoffResponseStep: 'Agent Handoff Response Step',
    },
    stepDetailsCardTitle: 'Step Details',
    stepSourcesCardTitle: 'Sources',
    threadCardTitle: 'Thread',
    timestampTitle: 'Timestamp',
    toolPlatformIdFieldName: 'Platform ID',
    applicationFieldName: 'Application',
    agentDetailsFieldName: 'Agent',
    emailFieldName: 'Email',
    findingsFieldName: 'Findings',
    ipAddressFieldName: 'IP Address',
    chatModeFieldName: 'Chat mode',
    toolOriginFieldName: 'Tool Origin',
    toolInvocationsDetailsServerFieldName: 'Server',
    toolInvocationsDetailsPathFieldName: 'HTTP Path',
    toolInvocationsDetailsMethodFieldName: 'HTTP Method',
    toolInvocationsDetailsOperationFieldName: 'Operation Schema',
    messageFieldName: 'Message',
    transcriptFetchingAllowedFieldName: 'Member of security group (at step timestamp)',
    attachments: 'Attachments',
    attachmentDeletedSuccessfully: 'Attachment deleted successfully',
    attachmentDeletionFailed: 'Deletion failed',
    attachmentDownloadedSuccessfully: 'Attachment downloaded successfully',
    attachmentDownloadFailed: 'Download failed',
    transcriptFetchingAllowedFieldDescription:
      'Zenity analyzes risks for messages that were accessible in real time. If the user was not a member of the security group at the time of the event, the message have not been analyzed.',
    nameFieldName: 'Actor',
    regionFieldName: 'Region',
    sensitivityLabelFieldName: 'Sensitivity Label',
    showPromptContentLinkName: 'Fetch Message Content',
    resourceTypeFieldName: 'Resource Type',
    threadIdFieldName: 'Thread Id',
    urlFieldName: 'URL',
    sourceIdFieldName: 'Source Id',
    noFindingsFound: 'No findings found',
    payload: 'Payload',
    fetchPayload: 'Fetch Payload Content',
    fetchToolInvocationResult: 'Fetch Result Content',
    fetchToolInvocationParams: 'Fetch Parameters Content',
    triggeringEvent: 'Triggering Event',
    invocationParameters: 'Invocation Parameters',
    toolInvocationResult: 'Invocation Result',
    callInvocationParameters: 'Call Request Parameters',
    toolCallResult: 'Tool Call Result',
    toolField: 'Tool',
    viewRelatedToolCallRequest: 'View related tool call request',
    fetchSourceSnippet: 'Fetch Source Snippet',
    ragQuery: 'RAG Query',
    fetchCitationSnippet: 'Fetch Citation Snippet',
    snippet: 'Snippet',
    environmentNameFieldName: 'Environment Name',
    environmentIdFieldName: 'Environment ID',
    m365CopilotExtensionAgent: 'M365 Copilot Extension Agent',
    m365CopilotExtensionAgentTooltip:
      "A custom AI agent created to extend Microsoft 365 Copilot's capabilities. It handles specific tasks declaratively while operating within the M365 environment.",
    agentStep: 'AI agent step',
    a2aCommunicationCardTitle: 'A2A Communication',
    requestingAgentFieldName: 'Requesting Agent',
    respondingAgentFieldName: 'Responding Agent',
    relatedStepFieldName: 'Related Step',
    viewRelatedHandoffRequest: 'View Agent Handoff Request Step',
    viewRelatedHandoffResponse: 'View Agent Handoff Response Step',
  },
  aiAgentStepTranscriptDialog: {
    title: 'Agent Step Transcript',
    description: 'This is the transcript of the agent step fetched from the platform.',
    confirmationDialogTitle: 'Agent Step Transcript Confirmation',
    confirmationDialogDescription:
      'The transcript may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the transcript is retrieved in real-time for display purposes only.',
    confirmationDialogNo: 'No',
    confirmationDialogYes: 'Yes',
    showRawData: 'Show Raw Data',
    sensitiveIconToolTip: 'Beginning of evidence',
  },
  aiAgentStepPayloadDialog: {
    title: 'Agent Step Payload',
    description: 'This is the payload of the agent step fetched from the platform.',
    confirmationDialogTitle: 'Agent Step Payload Confirmation',
    confirmationDialogDescription:
      'The payload may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the payload is retrieved in real-time for display purposes only.',
    confirmationDialogNo: 'No',
    confirmationDialogYes: 'Yes',
    showRawData: 'Show Raw Data',
    sensitiveIconToolTip: 'Beginning of evidence',
  },
  aiAgentStepToolInvocationResultDialog: {
    title: 'Agent Step Tool Invocation Result',
    description: 'This is the tool invocation result of the agent step fetched from the platform.',
    confirmationDialogTitle: 'Agent Step Tool Invocation Result Confirmation',
    confirmationDialogDescription:
      'The tool invocation result may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the tool invocation result is retrieved in real-time for display purposes only.',
    confirmationDialogNo: 'No',
    confirmationDialogYes: 'Yes',
    showRawData: 'Show Raw Data',
    sensitiveIconToolTip: 'Beginning of evidence',
  },
  aiAgentStepToolInvocationParamsDialog: {
    title: 'Agent Step Tool Invocation Parameters',
    description: 'This is the tool invocation parameters of the agent step fetched from the platform.',
    confirmationDialogTitle: 'Agent Step Tool Invocation Parameters Confirmation',
    confirmationDialogDescription:
      'The tool invocation parameters may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the tool invocation parameters is retrieved in real-time for display purposes only.',
    confirmationDialogNo: 'No',
    confirmationDialogYes: 'Yes',
    showRawData: 'Show Raw Data',
    sensitiveIconToolTip: 'Beginning of evidence',
  },
  aiAgentStepToolSourceSnippetsDialog: {
    title: 'Agent Step Source Snippets',
    description: 'This is the source snippets of the agent step fetched from the platform.',
    confirmationDialogTitle: 'Agent Step Tool Source Snippets Confirmation',
    confirmationDialogDescription:
      'The snippets may contain sensitive data. <br>Are you sure you want to continue? <br>Zenity does not store this data in any way, the snippets is retrieved in real-time for display purposes only.',
    confirmationDialogNo: 'No',
    confirmationDialogYes: 'Yes',
    showRawData: 'Show Raw Data',
    sensitiveIconToolTip: 'Beginning of evidence',
  },
  aiFindingPage: {
    runtimeFinding: 'Runtime Finding',
    noFindingsFound: 'No findings to display. Adjust your filters to explore more findings.',
    viewRelatedFindings: 'View Thread Findings',
    viewThread: 'View Thread',
    fixSuggestionCardTitle: 'Response Recommendation',
    idTitle: 'Id:',
    relevantAgentStepsCardTitle: 'Relevant Agent Steps',
    riskReasonCardTitle: 'Why is this a risk?',
    summaryCardTitle: 'Summary',
    timestampFieldName: 'Timestamp',
    investigateSuggestionCardTitle: 'How to investigate?',
    ruleIdLabel: 'Detection Rule Id',
    threadIdLabel: 'Thread Id',
    aiServiceLabel: 'AI Service',
    clientApplicationLabel: 'Client Application',
    agentNameLabel: 'Agent',
    stepTypeLabel: 'Step Type',
    stepDetailsLabel: 'Step Details',
    linkToStepDetails: 'View step details',
    regionLabel: 'Region',
    nameLabel: 'Actor',
    emailLabel: 'Email',
    riskReasonCardAsiOwaspFieldKey: 'OWASP Agentic AI',
  },
  commonEntitiesInfo: {
    resourceName: 'User-generated resource name as presented on the platform',
    resourceCategory: 'Resource type classification as part of Zenity cross-platform model',
    resourceType: 'The specific terminology used by the platform',
    platformResourceId: 'Resource id as presented on the platform',
    environment: 'A logical container to store and use different resources in the platform',
    createdBy: 'A link to the user which created the resource',
    ownedBy: 'A link to the user which owns the resource',
    orgWideAccess: 'Indicates whether the resource is accessible to the entire organization',
    publicAccess: 'Indicates whether the resource is accessible to the public internet',
    publicationStatus: 'The bot publication status indicates whether the bot is published or not',
    version: 'The version of the resource',
    userAccessAmount: 'Estimation for how many users have a direct or indirect access to the resource',
    businessCriticality:
      'Evaluates the potential business importance of an automation or application, using data Zenity collects about a resource',
    securityRisk: 'Evaluates the various security violations impacting a single resource',
    resourceSpecificAttributes: {
      identity: {
        identityType: 'The identity type as it appears on the platform',
        identityProviderId: 'The id of the identity assigned by the identity provider',
        email: 'The identity email which can be used to make contact',
        department: 'The department in which the user works',
        isActive: 'Checks if the User can perform actions on the organization resources protected by Zenity',
        isExternal: 'Checks if the user is a part of the organization Zenity protects',
        isBuilder: 'Checks if the user is a builder in the platform',
        totalMembersCount: 'The total number of members in the group',
      },
      connector: {
        providerName: 'The Company that built the connector',
        providerCategory: 'Relations between the source platform and the company that built the connector',
        version: 'The version of the connector',
        backendServiceUrl: 'The URL of the backend service behind the connector, relevant for custom connectors',
        isDeprecated:
          "Indicates whether the connector is deprecated or not. Deprecated components don't get security updates and could lead to malicious activity",
      },
      connection: {
        inUse:
          'Indicates whether the connection is used by other resources of categories such as automations and applications',
        connectionStatus: 'Whether the connection is active and usable',
        connectionAccount: 'The email address or account used for connection authentication',
        connectionHostName: 'The host name / domain / URI of the server / endpoint the connection connects to',
        authenticationMethod: 'The authentication type the connection connects with',
        connectorLink: 'The connector used to facilitate the connection',
        isOnPremises:
          'Indicates whether the connection is to an on-premises data source like agents/gateways that are not hosted in the source platform or its cloud',
      },
      automation: {
        stepsCount: 'The number of steps in the automation',
        automationType: 'The automation type as it appears on the platform',
        automationStatus: 'Whether the automation is active',
        automationLastRunStatus: 'The status of the last run of the automation',
        usesAiModel: 'Whether the automation uses an AI Model',
        isSolutionAware: 'Whether the automation is inside a solution',
        isBotTrigger: 'Whether automation triggers a bot',
      },
      application: {
        applicationType: 'The application type as it appears on the platform',
        version: 'The application version',
        quarantineStatus: 'The application quarantine status as it appears on the platform',
      },
      environment: {
        environmentType: 'The environment type as it appears on the platform',
      },
      dataStorage: {
        dataStorageType: 'The data storage type as it appears on the platform',
        entityName: 'The name of the data storage entity',
      },
      bot: {
        botType: 'The bot type as it appears on the platform',
        version: 'The bot version',
        autonomousTriggersCount: 'The number of autonomous triggers the bot has',
      },
    },
  },
  playbooks: {
    firstTime: 'There are no playbooks yet',
    description: 'Description',
    emptyResults: 'No results found',
    playbookWarningMessage: "Failed runs detected, please view the 'Execution History' for details",
    triggers: 'Triggers',
    trigger: 'Trigger',
    coverage: 'Coverage',
    actions: 'Actions',
    action: 'Action',
    created: 'Created',
    createPlaybook: 'Create playbook',
    lastExecution: 'Last Execution',
    status: 'Status',
    statusChangeTitle: 'Playbook status change',
    disableStatusMessage: 'Are you sure you want to disable the following playbook?',
    enableStatusMessage: 'Are you sure you want to enable the following playbook?',
    deleteTitle: 'Playbook Deletion',
    deleteMessage: 'Are you sure you want to delete the following playbook?',
    noExecutions: 'No Executions',
    yourPlaybooks: 'My Playbooks',
    executionHistory: 'Execution History',
    searchBoxText: 'Search by resource ID or violation ID',
    marketplace: 'Pre-Built Playbooks',
    executionStatus: 'Execution Status',
    playbookName: 'Playbook Name',
    maxPlaybooksMsg: 'Your tenant has reached the maximum playbooks quota',
    notAllowedToAddPlaybookMsg: 'You do not have sufficient permissions to add a playbook',
    executions: 'Executions',
    executionsNotFound: 'No executions were found',
    copyExecutionLog: 'Copy Log',
    updatePlaybookText: 'Update Playbook',
    updatePlaybookTriggerText: 'Update Playbook Trigger',
    createPlaybookText: 'Create New Playbook',
    multipleResourceTypeWarningText:
      "The selected options operates on different resource types. This will limit this playbook's supported actions.",
    duplicateAction: 'Clone',
    coverageTooltip:
      'Existing: Triggers when the playbook is created or edited, acting on existing violations/resources\n\nNew: Triggers at the end of scans, and continuously monitors for new violations/resources\n\nAll: Combines both behaviors for comprehensive coverage',
    secureFlowStepsTooltip: 'Action is supported for flows within a solution only and is only compatible with rules: ',
    removeAccessTooltip: 'Action is only compatible with guest access violations',
    waitActionTooltip: "Subsequent actions won't run if violation is resolved or exempted during wait period.",
    sendWebhookActionDisplayName: 'Export to webhook',
    sendToSocActionDisplayName: 'Send to SOC',
    deleteAgentActionDisplayName: 'Delete Agent',
    deletePowerBIDatasetActionDisplayName: 'Delete Dataset',
    deletePowerBIReportActionDisplayName: 'Delete Report',
    setCopilotAuthenticationActionDisplayName: 'Set Agent Authentication',
    addOwnerToCopilotActionDisplayName: 'Add Owner to Agent',
    removeAccessFromCopilotActionDisplayName: 'Remove Access From Agent',
    isolateFlowResourceActionDisplayName: 'Isolate Flow',
    isolateCopilotResourceActionDisplayName: 'Isolate Agent',
    isolateGptActionDisplayName: 'Isolate GPT',
    isolateWorkspaceActionDisplayName: 'Isolate Workspace',
    setAllUsersAccessWorkspaceActionDisplayName: 'Set Access For All',
    adminActionTooltip: 'This action is allowed for Admin only.',
    apiAction: 'This action is available with api-key or api-token authentication method',
    violationTriggerEditConfirmationMessage:
      'Editing this trigger will impact which violations are included in the playbook. Are you sure you want to proceed?',
    resourceTriggerEditConfirmationMessage:
      'Editing this trigger will impact which resources are included in the playbook. Are you sure you want to proceed?',
    projection: {
      title: 'Playbook Projections',
      totalViolations: 'Total Violations',
      totalResources: 'Total Resources',
      totalViolationsTooltip: 'Total violations impacted by this playbook',
      totalResourcesTooltip: 'Total resources impacted by this playbook',
      violationExplorationNotAvailable: 'The current playbook trigger settings do not allow violation exploration',
      resourceExplorationNotAvailable: 'The current playbook trigger settings do not allow resource exploration',
      insightsNotAvailable: 'Insights are available only for playbooks covering existing violations or resources',
      missingTrigger: 'Add a trigger to view playbook insights',
    },
    triggersConfigurationLabels: {
      labels: 'Violation Label',
      supportedResourceTypes: 'Supported resource types',
      deletedLabelsWaring:
        'The playbook is configured with labels that are already deleted. Please save the playbook again to avoid this warning in the future.',
      ruleUpdates: {
        title: 'Violation Rule Updates',
        description: "Include violations triggered by Zenity's internal rule updates",
        tooltip:
          "This setting helps prevent false spikes in violations when Zenity updates a detection rule. By choosing 'Exclude', you ensure that any new violations caused by internal rule changes (which are pre-existing issues which now generate new violations) won't trigger the playbook. Select 'Include' only if you want playbooks to run in response to such updates.",
      },
      violationRule: 'Violation Rule',
      ruleTag: 'Rule Tag',
      resourceType: 'Resource Type',
      resourceTypes: 'Resource Type',
      severity: 'Severity',
      user: 'User',
      connectors: 'Connectors',
      resourceStatus: 'Resource Status',
      resourceSecurityRisk: 'Resource Security Risk',
      securityRisk: 'Security Risk',
      resourceBusinessCriticality: 'Resource Business Criticality',
      businessCriticality: 'Business Criticality',
      resourceCreatedBy: 'Resource Created By',
      createdBy: 'Created By',
      resourceLastModifiedBy: 'Resource Last Modified By',
      lastModifiedBy: 'Last Modified By',
      resourceCreatedAt: 'Resource Created Time',
      createdAt: 'Created Time',
      resourceCreatedSince: 'Resource Created Since',
      createdSince: 'Created Since',
      resourceCreatedUntil: 'Resource Created Until',
      createdUntil: 'Created Until',
      resourceFirstSeenAt: 'Resource First Seen Time',
      firstSeenAt: 'First Seen Time',
      resourceFirstSeenSince: 'Resource First Seen Since',
      firstSeenSince: 'First Seen Since',
      resourceFirstSeenUntil: 'Resource First Seen Until',
      firstSeenUntil: 'First Seen Until',
      resourceGenerativeAiOptions: 'Resource Generative AI Options',
      generativeAiOptions: 'Generative AI Options',
      triggerSettings: 'Trigger Settings',
      ruleIds: 'Rule IDs',
      ruleSeverity: 'Rule Severity',
      aiServices: 'AI Services',
      selectRules: 'Select Rules',
      selectSeverity: 'Select Severity',
      selectAiServices: 'Select AI Services',
      selectActors: 'Select Actors',
      enterActorEmail: 'Enter Actor Email',
      findingTriggerDescription: 'Finding Trigger Description',
      findingTriggerDescriptionText:
        'Configure triggers for AI findings. Select rules, severity levels, AI services, or specific actors to monitor.',
    },
    triggerSettings: 'Trigger Settings',
    ruleIds: 'Rule IDs',
    ruleSeverity: 'Rule Severity',
    aiServices: 'AI Services',
    selectRules: 'Select Rules',
    selectSeverity: 'Select Severity',
    selectAiServices: 'Select AI Services',
    selectActors: 'Select Actors',
    enterActorEmail: 'Enter Actor Email',
    findingTriggerDescription: 'Finding Trigger Description',
    findingTriggerDescriptionText:
      'Configure triggers for AI findings. Select rules, severity levels, AI services, or specific actors to monitor.',
    findingsPolicyInfo:
      'Runtime finding triggers automatically use the Default policy. Custom policy selection is not supported.',
    issuePolicyInfo: 'Issue triggers automatically use the Default policy. Custom policy selection is not supported.',
    selectIssueSeverity: 'Select Issue Severity',
    selectTaxonomy: 'Select Taxonomy',
    taxonomy: 'Taxonomy',
  },
  playbooksMarketplace: {
    title: 'Recommended playbooks',
    selectTemplateActionDisplayName: 'Use this Playbook',
    noTemplatesAvailableMessage: 'No available playbooks for current active integrations',
    playbookTemplates: {
      silentRemediationTitle: 'Silent Remediation',
      silentRemediationSubTitle: 'Automatically secure sensitive steps leaking data to logs',
      silentRemediationDescription:
        'Automatically detects and secures flows that are exposing sensitive data in logs or have insecure steps handling secrets. The playbook will secure the flow steps, send an email notification, and add a "Remediation-Performed" label. Excludes violations already marked as False-Positive.',
      agentSetAuthenticationTitle: 'Agent Set Authentication',
      agentSetAuthenticationSubTitle: 'Automatically enforces secure authentication configuration for agents',
      agentSetAuthenticationDescription:
        'Automatically detects agents that accept unauthenticated chat or have custom authentication not enforced. The playbook will set the agent authentication to "Only for Teams and PowerApps" (enforcing AAD authentication & restricting org-wide access), send an email notification, and add a "Remediation-Performed" label. Excludes violations already marked as False-Positive.',
      removeOrgWideAccessTitle: 'Remove Org-Wide Access (Agents)',
      removeOrgWideAccessSubTitle: 'Remove org-wide level access from agent',
      removeOrgWideAccessDescription:
        'Automatically detects agents that are accessible by the entire tenant. The playbook will remove tenant-wide access from the agent, send an email notification, and add a "Remediation-Performed" label. Excludes violations already marked as False-Positive.',
      removeOrgWideAccessAppsTitle: 'Remove Org-Wide Access (Apps)',
      removeOrgWideAccessAppsSubTitle: 'Remove org-wide level access from application',
      removeOrgWideAccessAppsDescription:
        'Automatically detects agents that are consuming uploaded files with potentially sensitive data (PHI - Protected Health Information). The playbook will remove tenant-wide access from the agent, send an email notification, and add a "Remediation-Performed" label. Excludes violations already marked as False-Positive.',
      removeOrgWideAccessConnectionsTitle: 'Remove Org-Wide Access (Connections)',
      removeOrgWideAccessConnectionsSubTitle: 'Remove org-wide level access from connections',
      removeOrgWideAccessConnectionsDescription:
        'Automatically detects connections that are accessible by the entire tenant. The playbook will remove tenant-wide access from the connection, send an email notification, and add a "Remediation-Performed" label. Excludes violations already marked as False-Positive.',
    },
  },
  playbookExecutions: {
    triggeredEntity: 'Triggered Entity',
    failedAction: 'Failed Action',
    logs: 'Execution Logs',
    resourceName: 'Resource Name',
    resourceType: 'Resource Type',
    environment: 'Environment',
    violationStatus: 'Violation Status',
    violationStatusTooltip: 'Applicable only to violations',
    violationStatusDescription: 'Shows the violation state after the playbook execution',
  },
  keyboardShortcuts: {
    violations: tableSidePanelShortcuts('violation'),
    resources: tableSidePanelShortcuts('resource'),
    agentSteps: tableSidePanelShortcuts('step'),
    findings: tableSidePanelShortcuts('finding'),
    issues: tableSidePanelShortcuts('issue'),
    dataItems: tableSidePanelShortcuts('data item'),
    prevention: tableSidePanelShortcuts('prevention'),
    inLineControls: tableSidePanelShortcuts('in-line control'),
  },
  zenityCommon: {
    platform: 'Platform',
    integration: 'Integration',
    policies: 'Policies',
    policy: 'Policy',
    playbook: 'Playbook',
    playbooks: 'Playbooks',
    resourceType: 'Resource Type',
    resourceTypeDisplayNames: {
      copilot: 'Agent',
      copilotAction: 'Agent Action',
      copilotKnowledge: 'Agent Knowledge',
      copilotTopic: 'Agent Topic',
    },
  },
  emailTemplatePlaceholders: {
    violationName: {
      label: '%VIOLATION_NAME%',
      description: 'Violation name',
    },
    violationId: {
      label: '%VIOLATION_ID%',
      description: 'Violation ID',
    },
    violationSummary: {
      label: '%VIOLATION_SUMMARY%',
      description: 'Explanation of what happened',
    },
    violationSeverity: {
      label: '%VIOLATION_SEVERITY%',
      description: 'Violation severity',
    },
    risk: {
      label: '%RISK%',
      description: 'Explanation of the potential risk',
    },
    triage: {
      label: '%TRIAGE%',
      description: 'Explanation on how to triage',
    },
    violationFix: {
      label: '%HOW_TO_FIX%',
      description: 'Explanation of how to mitigate the violation',
    },
    violationFirstSeen: {
      label: '%VIOLATION_FIRST_SEEN%',
      description: 'Time violation was first seen by Zenity',
    },
    resourceType: {
      label: '%RESOURCE_TYPE%',
      description: 'Resource type',
    },
    linkToPlatform: {
      label: '%LINK_TO_PLATFORM%',
      description: 'Link to original platform resource',
    },
    linkToZenity: {
      label: '%LINK_TO_ZENITY%',
      description: 'Link to Zenity',
    },
    resourceName: {
      label: '%RESOURCE_NAME%',
      description: 'Resource name',
    },
    resourceId: {
      label: '%RESOURCE_ID%',
      description: 'Resource id in Zenity inventory',
    },
    resourceCategory: {
      label: '%RESOURCE_CATEGORY%',
      description: 'Resource category',
    },
    environmentName: {
      label: '%ENVIRONMENT_NAME%',
      description: 'Environment name',
    },
    environmentType: {
      label: '%ENVIRONMENT_TYPE%',
      description: 'Environment type',
    },
    inventoryResourceLink: {
      label: '%INVENTORY_RESOURCE_LINK%',
      description: 'Link to Zenity inventory resource',
    },
    resourceFirstSeen: {
      label: '%RESOURCE_FIRST_SEEN_AT%',
      description: 'Time resource was first seen by Zenity',
    },
    resourceCreatedAt: {
      label: '%CREATED_AT%',
      description: 'Resource creation time',
    },
    resourcesCreatedBy: {
      label: '%CREATED_BY%',
      description: 'Resource creator',
    },
    findingName: {
      label: '%FINDING_RULE_NAME%',
      description: 'Finding name',
    },
    findingSeverity: {
      label: '%FINDING_SEVERITY%',
      description: 'Finding severity',
    },
    findingTimestamp: {
      label: '%FINDING_TIMESTAMP%',
      description: 'Time the finding was detected',
    },
    aiService: {
      label: '%AI_SERVICE%',
      description: 'AI service that generated the finding',
    },
    findingRuleSummary: {
      label: '%FINDING_RULE_SUMMARY%',
      description: 'Summary of the finding rule',
    },
    findingRuleResponseRecommendation: {
      label: '%FINDING_RULE_RESPONSE_RECOMMENDATION%',
      description: 'Recommended response for the finding',
    },
    findingRuleHowToInvestigate: {
      label: '%FINDING_RULE_HOW_TO_INVESTIGATE%',
      description: 'How to investigate the finding',
    },
    agentName: {
      label: '%AGENT_NAME%',
      description: 'Name of the AI agent',
    },
    actorEmail: {
      label: '%ACTOR_EMAIL%',
      description: 'Email of the user interacting with the agent',
    },
    findingCoreEvidence: {
      label: '%FINDING_CORE_EVIDENCE%',
      description: 'Evidence of the finding',
    },
    secretType: {
      label: '%SECRET_TYPE%',
      description: 'Type of secret detected in the finding (for secret detection related findings)',
    },
    informationClassification: {
      label: '%INFORMATION_CLASSIFICATION%',
      description: 'Classification of the sensitive information detected (for sensitive information related findings)',
    },
    issueName: {
      label: '%ISSUE_NAME%',
      description: 'Issue name',
    },
    issueSeverity: {
      label: '%ISSUE_SEVERITY%',
      description: 'Issue severity',
    },
    issueTaxonomy: {
      label: '%ISSUE_TAXONOMY%',
      description: 'Issue taxonomy classification',
    },
    issueDescription: {
      label: '%ISSUE_DESCRIPTION%',
      description: 'Issue description',
    },
    issueActorName: {
      label: '%ACTOR_NAME%',
      description: 'Name of the actor involved in the issue',
    },
    issueActorEmail: {
      label: '%ACTOR_EMAIL%',
      description: 'Email of the actor involved in the issue',
    },
    issueRuleId: {
      label: '%ISSUE_RULE_ID%',
      description: 'Rule ID that triggered the issue',
    },
    issueResourceName: {
      label: '%ISSUE_RESOURCE_NAME%',
      description: 'Name of the primary resource associated with the issue',
    },
  },
  authErrors: {
    // heading text
    headingGeneric: 'Something went wrong.',
    // Secret Expired case
    headingSecretExpired: 'The login attempt has failed.',
    descriptionSecretExpiredFirstLine:
      'SSO integrations usually use secrets that expire after a few months. Please check that your',
    descriptionSecretExpiredSecondLine:
      'secret has not expired. If it has expired, create a new one and contact Zenity to update it.',
    // Invitation Expired case
    headingInvitationExpired: 'The invitation link has expired.',
    descriptionInvitationExpiredFirstLine:
      "It's likely that the invitation link you've received has expired. Invitation links are valid only for 1 week",
    descriptionInvitationExpiredSecondLine:
      "after creation. Please contact your company's Zenity administrator to receive a new invite link to your inbox.",
    // User Is Not In Organization case
    headingNotInOrganization: 'Access Required.',
    descriptionNotInOrganizationFirstLine:
      "The user lacks the appropriate permissions to access the app. Please contact your company's",
    descriptionNotInOrganizationSecondLine:
      'Zenity administrator to be granted access and receive a new invite link to your inbox.',
    // footer text
    footerSupport: 'For any additional support, please reach out to us at ',
    zenitySupportEmail: 'support@zenity.io',
  },
  noAccess: {
    heading: 'No Access',
    descriptionLine1: `You don't have any roles assigned to your user.`,
    descriptionLine2: 'Please contact your administrator to get access to the platform.',
    contactAdmin: 'For any additional support, please reach out to us at ',
    supportEmail: 'support@zenity.io',
  },
  sensitiveDataInput: {
    description: 'This may contain sensitive user content, which is fetched in real-time and not stored.',
    descriptionViewer:
      'Data may contain sensitive user content, which is fetched in real-time and not stored. You are not authorized to fetch this content.',
    btnText: 'Fetch Content',
    outpostDisabledTooltip: 'Content fetching is not supported for outpost integrations',
    downloadTooltip: 'Download original file',
    downloadSuccess: 'File download started',
  },
  inLineControlRulesPage: {
    title: 'In-Line Control Rules',
  },
  enterpriseAIThreads: {
    fetchContent: 'Fetch content',
    fetchContentDescription: 'Data may contain sensitive user content, which is fetched in real-time and not stored.',
    fetchContentDescriptionViewer:
      'Data may contain sensitive user content, which is fetched in real-time and not stored. You are not authorized to fetch this content.',
    fetchContentError: 'Content fetching was unsuccessful. A server error may have occurred. Please try again.',
    tryAgain: 'Try again',
    stepContentError: "Warning: Couldn't retrieve content: {error}",
    stepContentErrorGeneric: "Warning: Some content couldn't be retrieved.",
    fetchContentPartialFailure: '{succeeded} of {total} steps fetched. {failed} could not be retrieved.',
    fetchContentForbidden: 'This feature is disabled for your account.',
    fetchContentNotFound: 'Content is no longer available due to retention policy.',
    fetchContentUnprocessable: 'The request could not be processed.',
    fetchContentRateLimited: 'Rate limited by the AI platform. Please try again later.',
  },
  attackSimulationsPage: {
    pageTitle: 'AI Red Teaming',
    pageSubtitle:
      'Simulate real user behavior to identify' + ' sensitive data exposure in AI assistants' + ' and agents.',
    newAssessmentButton: 'Create assessment',
    searchPlaceholder: 'Search assessments...',
    emptyStateTitle: 'No assessments yet',
    emptyStateMessage: 'Run your first assessment to proactively monitor sensitive data exposure.',
    columns: {
      assessment: 'Assessment',
      aiService: 'AI service',
      attackStrategy: 'Goal',
      status: 'Status',
      results: 'Results',
      lastRun: 'Created at',
    },
    statuses: {
      pending: 'Pending',
      executing: 'In progress',
      finishedSuccess: 'Completed',
      finishedFail: 'Failed',
    },
    createDialog: {
      title: 'Create assessment',
      infoBannerLine1: 'Creating an assessment requires a dedicated' + ' user in the target platform.',
      infoBannerLine2: "If you don't already have one," + ' create it before continuing.',
      testNameLabel: 'Test name',
      testNamePlaceholder: 'Sensitive info test',
      aiServiceLabel: 'AI service',
      aiServiceValue: 'Copilot for Microsoft 365',
      comingSoonBadge: 'More services & scenarios coming soon!',
      testScenarioLabel: 'Test scenario',
      testScenarioName: 'Sensitive data hunting',
      testScenarioDesc: 'Test if the AI exposes confidential files,' + ' PII, or restricted information',
      attackStrategyLabel: 'Goal',
      strategies: {
        general: 'General',
        generalDesc: 'Sensitive documents, internal data,' + ' credentials, restricted information',
        hrData: 'HR data',
        hrDataDesc: 'Employee records, performance' + ' reviews, compensation',
        financialData: 'Financial data',
        financialDataDesc: 'Budgets, invoices, internal reports',
        custom: 'Custom',
        generalHint: 'For more accurate results,' + ' choose an attack-based goal',
        customPlaceholder: 'e.g., Identify files that violate' + ' PCI compliance',
      },
      userAuthLabel: 'User authentication',
      userAuthDesc: 'Sign in with a dedicated test user' + ' to run the assessment.',
      signInWithMicrosoft: 'Sign in with Microsoft',
      connectedAs: 'Connected as',
      connected: 'Connected',
      changeUser: 'Change user',
      adminConsentError:
        "Your organization's admin needs to approve" +
        ' this app before you can sign in.' +
        ' Please contact your IT administrator.',
      signInError: 'Sign-in failed. Please try again.',
      useTestAccount: 'Use test account (dev only)',
      sessionSafetyTitle: 'Session Safety',
      sessionSafetyItems: {
        userScoped: 'User-scoped permissions only',
        readOnly: 'Read-only simulation',
        noPersistentChanges: 'No persistent changes to Copilot configuration',
      },
      cancelButton: 'Cancel',
      runAssessmentButton: 'Run assessment',
    },
    drawer: {
      statusLabel: 'Status',
      aiServiceLabel: 'AI service',
      attackStrategyLabel: 'Goal',
      testUserLabel: 'Test user',
      testScenariosLabel: 'Test scenarios',
      inProgressTitle: 'Assessment is in progress.',
      inProgressSubtitle:
        'This may take some time to complete.' + ' You can leave this page and check' + ' back later for results.',
      steps: {
        creatingSession: 'Creating AI session',
        executingPrompts: 'Executing attack scenarios',
        analyzingResponses: 'Analyzing AI responses',
        detectingSensitiveData: 'Detecting sensitive data',
      },
      safetyNotice: 'This assessment runs in an isolated' + ' session that mimics real user' + ' interactions.',
      safetyNoticeDetail: 'All prompts and responses are logged' + ' for analysis and compliance purposes.',
      resultsTitle: 'Results',
      findingsFound: 'findings found',
      exportToCsv: 'Export to CSV',
      colFileName: 'Name',
      colSensitivity: 'Sensitivity',
      colSeverity: 'Severity',
      colLastSeen: 'Last seen',
      colLocation: 'Location',
      location: 'Location',
      description: 'Description',
      evidence: 'Evidence',
      noFindings: 'No findings',
      noFindingsMessage: 'No sensitive data exposure was detected' + ' during this assessment.',
      assessmentFailed: 'Assessment failed',
      assessmentFailedMessage: 'The assessment could not be completed.' + ' Please try running it again.',
    },
  },
};

export class Label {
  public static get = (label: string) => _.get(Labels, label);

  public static getRawResourceTextFromLabel(resourceType: ResourceType): IGetSpecificRawDataLabels {
    const resourceTypeString = Label._camelCaseToCapitalized(resourceType);
    return {
      title: _.get(Labels, 'commonActions.getRawResourceAction.getRawResourceSpecificResourceTypeText.title').replace(
        /\{0\}/g,
        resourceTypeString
      ),
      rawJsonDescription: _.get(
        Labels,
        'commonActions.getRawResourceAction.getRawResourceSpecificResourceTypeText.rawJsonDescription'
      ).replace(/\{0\}/g, resourceTypeString),
    } as IGetSpecificRawDataLabels;
  }

  private static _camelCaseToCapitalized(camelCaseString: string): string {
    return camelCaseString
      .replace(/[A-Z]/g, (match) => ` ${match}`)
      .replace(camelCaseString.charAt(0), camelCaseString.charAt(0).toUpperCase());
  }
}
